import * as _Astronomy from 'astronomy-engine';
const Astronomy: any = (_Astronomy as any).MakeTime ? _Astronomy : ((_Astronomy as any).default || _Astronomy);
import {
  BirthDetails,
  CompleteKundali,
  NodeConvention,
  CalculationContext,
  HouseInfo,
  PanchangInfo,
  PlanetName,
  PlanetPosition,
  ZodiacSign,
  AvakhadaChakra,
  DashaPeriod,
  ManglikAnalysis,
  KaalSarpAnalysis,
  SadeSatiAnalysis,
  SadeSatiDhaiyyaPeriod,
  PitraDoshaAnalysis,
  GemstoneRecommendation,
  AshtakvargaData,
  KakshyaTransitInfo,
  KundaliChartData,
  ShadbalaData,
  ShadbalaItem,
  BhavaBalaItem,
  CharaKarakaItem,
  PlanetaryAvastha,
  PrashnaResult,
  GocharItem,
  GocharTransitData,
  DoubleTransitPeriod,
  CalculationProvenance,
  VargaBhedaRecord,
  ShashtiamshaDeityInfo,
  CharaDashaData,
  CharaDashaPeriod
} from '../types/kundali';
import {
  NATURAL_FRIENDSHIPS,
  getPanchadhaMaitriCompound,
  DignityLevel
} from './planetaryDignityEngine';
import {
  calculateCanonicalTithi,
  calculateCanonicalNakshatra,
  calculateCanonicalYoga,
  calculateCanonicalKarana,
  calculateCanonicalVara,
  calculateAuthoritativeSunTimes,
  getCanonicalAyanamsha
} from './panchangaEngine';
import { formatTime } from './dateUtils';
import { calculateShadbala, classifyAshtavidhaMotion } from './shadbalaEngine';
import {
  evaluateAllYogasAndDoshas,
  evaluateManglikDosha,
  evaluateKaalSarpYoga
} from './yogaDoshaEngine';
import { evaluateAllGocharVedha } from './gocharVedhaEngine';

export const ZODIAC_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

export const ZODIAC_LORDS: { [key in ZodiacSign]: string } = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

export const NAKSHATRAS = [
  { name: 'Ashwini', lord: 'Ketu' },
  { name: 'Bharani', lord: 'Venus' },
  { name: 'Krittika', lord: 'Sun' },
  { name: 'Rohini', lord: 'Moon' },
  { name: 'Mrigashira', lord: 'Mars' },
  { name: 'Ardra', lord: 'Rahu' },
  { name: 'Punarvasu', lord: 'Jupiter' },
  { name: 'Pushya', lord: 'Saturn' },
  { name: 'Ashlesha', lord: 'Mercury' },
  { name: 'Magha', lord: 'Ketu' },
  { name: 'Purva Phalguni', lord: 'Venus' },
  { name: 'Uttara Phalguni', lord: 'Sun' },
  { name: 'Hasta', lord: 'Moon' },
  { name: 'Chitra', lord: 'Mars' },
  { name: 'Swati', lord: 'Rahu' },
  { name: 'Vishakha', lord: 'Jupiter' },
  { name: 'Anuradha', lord: 'Saturn' },
  { name: 'Jyeshta', lord: 'Mercury' },
  { name: 'Mula', lord: 'Ketu' },
  { name: 'Purva Ashadha', lord: 'Venus' },
  { name: 'Uttara Ashadha', lord: 'Sun' },
  { name: 'Shravana', lord: 'Moon' },
  { name: 'Dhanishta', lord: 'Mars' },
  { name: 'Shatabhisha', lord: 'Rahu' },
  { name: 'Purva Bhadrapada', lord: 'Jupiter' },
  { name: 'Uttara Bhadrapada', lord: 'Saturn' },
  { name: 'Revati', lord: 'Mercury' }
];

export const PLANET_SYMBOLS: { [key in PlanetName]: string } = {
  Ascendant: 'Asc',
  Sun: 'Su',
  Moon: 'Mo',
  Mars: 'Ma',
  Mercury: 'Me',
  Jupiter: 'Ju',
  Venus: 'Ve',
  Saturn: 'Sa',
  Rahu: 'Ra',
  Ketu: 'Ke'
};

// DASHA YEARS (Vimshottari)
export const DASHA_ORDER: { planet: PlanetName; years: number }[] = [
  { planet: 'Ketu', years: 7 },
  { planet: 'Venus', years: 20 },
  { planet: 'Sun', years: 6 },
  { planet: 'Moon', years: 10 },
  { planet: 'Mars', years: 7 },
  { planet: 'Rahu', years: 18 },
  { planet: 'Jupiter', years: 16 },
  { planet: 'Saturn', years: 19 },
  { planet: 'Mercury', years: 17 }
];

/**
 * Calculates Ayanamsha for a Julian Date based on user preference (Lahiri, KP, Raman)
 */
export function getActiveAyanamsha(time: _Astronomy.AstroTime, systemOverride?: string): number {
  return getCanonicalAyanamsha(time, systemOverride);
}

/**
 * Resolves active Ayanamsha preference from localStorage or canonical default ('lahiri')
 */
export function getActiveAyanamshaPreference(): string {
  if (typeof localStorage !== 'undefined') {
    try {
      const pref = localStorage.getItem('astro_ayanamsha_pref');
      if (pref === 'lahiri' || pref === 'kp' || pref === 'raman') return pref;
    } catch {}
  }
  return 'lahiri';
}

/**
 * Resolves active Lunar Node convention from localStorage or canonical default ('mean')
 */
export function getActiveNodeConvention(): NodeConvention {
  if (typeof localStorage !== 'undefined') {
    try {
      const pref = localStorage.getItem('astro_node_pref');
      if (pref === 'true' || pref === 'mean') return pref;
    } catch {}
  }
  return 'mean';
}

/**
 * Calculates Lahiri Ayanamsha for a Julian Date
 */
export function getLahiriAyanamsha(time: _Astronomy.AstroTime): number {
  return getActiveAyanamsha(time, 'lahiri');
}

/**
 * Converts degree (0-360) to degree formatted string e.g. 14° 22' 15"
 */
export function formatDegree(deg: number): string {
  const d = Math.floor(deg);
  const mFull = (deg - d) * 60;
  const m = Math.floor(mFull);
  const s = Math.round((mFull - m) * 60);
  return `${d}° ${m}' ${s}"`;
}

/**
 * Calculates local sidereal time (LST) and Ascendant (Lagna)
 */
export function calculateAscendant(date: Date, lat: number, lon: number, ayanamsha: number): number {
  const time = Astronomy.MakeTime(date);
  const siderealTime = Astronomy.SiderealTime(time); // Greenwhich Apparent Sidereal Time in hours
  const localSiderealTimeHours = (siderealTime + lon / 15.0 + 24.0) % 24.0;
  const lstDeg = localSiderealTimeHours * 15.0; // in degrees

  // Obliquity of Ecliptic: Use true obliquity of date from Astronomy Engine
  const rad = Math.PI / 180;
  const tilt = Astronomy.e_tilt(time);
  const eps = ((tilt && typeof tilt.tobl === 'number') ? tilt.tobl : 23.4392911) * rad;
  const phi = lat * rad;
  const theta = lstDeg * rad;

  // Formula for Ascendant Longitude:
  // tan(Asc) = cos(theta) / (-sin(eps)*tan(phi) - cos(eps)*sin(theta))
  const y = Math.cos(theta);
  const x = -Math.sin(eps) * Math.tan(phi) - Math.cos(eps) * Math.sin(theta);
  let ascTropicalRad = Math.atan2(y, x);
  let ascTropicalDeg = (ascTropicalRad / rad + 360) % 360;

  // Subtract Sidereal Ayanamsha for Sidereal Ascendant
  let ascSiderealDeg = (ascTropicalDeg - ayanamsha + 360) % 360;
  return ascSiderealDeg;
}

/**
 * Parashari Drishti Rules for 9 Grahas (1-indexed house numbers 1-12)
 * Full Parashari Special Aspects:
 * - All planets aspect 7th house
 * - Mars aspects 4th and 8th
 * - Jupiter, Rahu, Ketu aspect 5th and 9th
 * - Saturn aspects 3rd and 10th
 */
export function getSpecialDrishtiHouses(planet: PlanetName, placedHouse: number): number[] {
  if (planet === 'Ascendant') return [];
  const drishti: number[] = [((placedHouse + 6 - 1) % 12) + 1]; // All planets aspect 7th house

  switch (planet) {
    case 'Mars':
      // 4th and 8th aspects
      drishti.push(((placedHouse + 4 - 1) % 12) + 1);
      drishti.push(((placedHouse + 8 - 1) % 12) + 1);
      break;
    case 'Jupiter':
    case 'Rahu':
    case 'Ketu':
      // 5th and 9th aspects
      drishti.push(((placedHouse + 5 - 1) % 12) + 1);
      drishti.push(((placedHouse + 9 - 1) % 12) + 1);
      break;
    case 'Saturn':
      // 3rd and 10th aspects
      drishti.push(((placedHouse + 3 - 1) % 12) + 1);
      drishti.push(((placedHouse + 10 - 1) % 12) + 1);
      break;
  }

  return Array.from(new Set(drishti)).sort((a, b) => a - b);
}

/**
 * Calculate Double Transit (Guru-Shani Gochar) influence for a specific target date.
 * Double Transit occurs when BOTH transiting Jupiter and transiting Saturn
 * simultaneously aspect or occupy a house relative to Natal Lagna for the given transit date.
 */
export function calculateDoubleTransit(
  natalPlanetsOrAscSignIndex: PlanetPosition[] | number,
  transitDate: Date = new Date(),
  context?: CalculationContext | NodeConvention
): number[] {
  let ascSignIndex = 0;
  if (typeof natalPlanetsOrAscSignIndex === 'number') {
    ascSignIndex = (natalPlanetsOrAscSignIndex % 12 + 12) % 12;
  } else if (Array.isArray(natalPlanetsOrAscSignIndex)) {
    const asc = natalPlanetsOrAscSignIndex.find(p => p.name === 'Ascendant');
    ascSignIndex = asc ? asc.signIndex : 0;
  }

  // Authoritative live planetary ephemeris for the transit date
  const liveEphem = calculateLivePlanetaryEphemeris(transitDate, context);
  const transitJupiter = liveEphem.find(p => p.planet === 'Jupiter');
  const transitSaturn = liveEphem.find(p => p.planet === 'Saturn');

  if (!transitJupiter || !transitSaturn) return [];

  // House positions from Natal Lagna (1-12)
  const jupiterHouse = ((transitJupiter.signIndex - ascSignIndex + 12) % 12) + 1;
  const saturnHouse = ((transitSaturn.signIndex - ascSignIndex + 12) % 12) + 1;

  // Parashari Drishti + Occupation for Transiting Jupiter (1, 5, 7, 9 from transit house)
  const jupiterAspects = [
    jupiterHouse,
    ((jupiterHouse + 4 - 1) % 12) + 1,
    ((jupiterHouse + 6 - 1) % 12) + 1,
    ((jupiterHouse + 8 - 1) % 12) + 1,
  ];

  // Parashari Drishti + Occupation for Transiting Saturn (1, 3, 7, 10 from transit house)
  const saturnAspects = [
    saturnHouse,
    ((saturnHouse + 2 - 1) % 12) + 1,
    ((saturnHouse + 6 - 1) % 12) + 1,
    ((saturnHouse + 9 - 1) % 12) + 1,
  ];

  // Common houses receiving simultaneous Double Transit
  const doubleTransitHouses = jupiterAspects.filter(h => saturnAspects.includes(h));
  return [...new Set(doubleTransitHouses)].sort((a, b) => a - b);
}

/**
 * Calculate Double Transit Periods for the next 3 years
 */
export function calculateDoubleTransitPeriods(
  ascendantDeg: number,
  natalAyanamsha?: number,
  ayanamshaSystem?: string
): DoubleTransitPeriod[] {
  const periods: DoubleTransitPeriod[] = [];
  const now = new Date();
  const threeYearsLater = new Date(now.getFullYear() + 3, now.getMonth(), now.getDate());
  
  let currentPeriod: DoubleTransitPeriod | null = null;
  const ascSignIdx = Math.floor(((ascendantDeg % 360) + 360) % 360 / 30);
  
  // Scan every 15 days for better resolution of transitions
  for (let d = new Date(now); d <= threeYearsLater; d.setDate(d.getDate() + 15)) {
    const time = Astronomy.MakeTime(d);
    // Instantaneous transit ayanamsha for the transit date ensures historical birth charts do not project historical ayanamsha onto future transits
    const transitAyanamsha = getActiveAyanamsha(time, ayanamshaSystem);
    const jupiterEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Jupiter, time, true));
    const saturnEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Saturn, time, true));
    
    const jLong = (jupiterEcl.elon - transitAyanamsha + 360) % 360;
    const sLong = (saturnEcl.elon - transitAyanamsha + 360) % 360;
    
    const jSignIdx = Math.floor(jLong / 30);
    const sSignIdx = Math.floor(sLong / 30);
    
    // Whole Sign houses relative to Natal Lagna (consistent with calculateDoubleTransit)
    const jHouse = ((jSignIdx - ascSignIdx + 12) % 12) + 1;
    const sHouse = ((sSignIdx - ascSignIdx + 12) % 12) + 1;
    
    const jAspects = [jHouse, ((jHouse + 4 - 1) % 12) + 1, ((jHouse + 6 - 1) % 12) + 1, ((jHouse + 8 - 1) % 12) + 1];
    const sAspects = [sHouse, ((sHouse + 2 - 1) % 12) + 1, ((sHouse + 6 - 1) % 12) + 1, ((sHouse + 9 - 1) % 12) + 1];
    
    const commonHouses = jAspects.filter(h => sAspects.includes(h)).sort((a, b) => a - b);

    if (commonHouses.length > 0) {
      const housesKey = commonHouses.join(',');
      if (!currentPeriod || currentPeriod.houses.join(',') !== housesKey) {
        if (currentPeriod) {
          currentPeriod.endDate = d.toISOString().split('T')[0];
          periods.push(currentPeriod);
        }
        currentPeriod = {
          startDate: d.toISOString().split('T')[0],
          endDate: threeYearsLater.toISOString().split('T')[0],
          houses: commonHouses,
          signs: commonHouses.map(h => ZODIAC_SIGNS[(ascSignIdx + h - 1) % 12])
        };
      }
    } else {
      if (currentPeriod) {
        currentPeriod.endDate = d.toISOString().split('T')[0];
        periods.push(currentPeriod);
        currentPeriod = null;
      }
    }
  }
  
  if (currentPeriod) periods.push(currentPeriod);
  
  return periods;
}

/**
 * Canonical helper to obtain authoritative birth UTC Date from BirthDetails.
 * Guarantees zero wall-clock leakage and deterministic temporal anchoring.
 */
export function getBirthUtcDate(details: BirthDetails): Date {
  const [year, month, day] = details.date.split('-').map(Number);
  const [hour, minute, second] = details.time.split(':').map(Number);
  const tzOffset = details.timezoneOffsetMinutes ?? 330;
  const localDateMs = Date.UTC(year, month - 1, day, hour, minute, second || 0);
  const utcDateMs = localDateMs - tzOffset * 60 * 1000;
  return new Date(utcDateMs);
}

/**
 * Primary Vedic Kundali calculation routine
 */
export function calculateKundali(
  details: BirthDetails,
  context?: CalculationContext | NodeConvention
): CompleteKundali {
  const birthUtcDate = getBirthUtcDate(details);
  const utcDateMs = birthUtcDate.getTime();
  const tzOffset = details.timezoneOffsetMinutes ?? 330;

  const time = Astronomy.MakeTime(birthUtcDate);
  const ayanamshaSystem: string =
    (typeof context === 'object' && context !== null ? context.ayanamsha : undefined) ||
    details.ayanamsha ||
    getActiveAyanamshaPreference();
  const ayanamsha = getActiveAyanamsha(time, ayanamshaSystem);

  // Calculate Tropical Body Longitudes using Astronomy Engine
  const sunEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, time, true));
  const moonEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Moon, time, true));
  const marsEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Mars, time, true));
  const mercuryEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Mercury, time, true));
  const jupiterEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Jupiter, time, true));
  const venusEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Venus, time, true));
  const saturnEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Saturn, time, true));

  // Resolve Node Convention strictly via explicit context, details, or active preference
  const convention: NodeConvention = 
    (typeof context === 'string' ? context : context?.nodeConvention) ||
    details.nodeConvention ||
    getActiveNodeConvention();

  const j2000Days = time.ut;
  let rahuTropicalDeg = (125.04452 - 0.0529538083 * j2000Days + 3600) % 360;

  if (convention === 'true') {
    // True (Oscillating) Lunar Node adjustment using periodic solar-lunar perturbations
    const sunMeanLong = (280.46646 + 0.98564736 * j2000Days) % 360;
    const rad = Math.PI / 180;
    const correction = -1.4979 * Math.sin(2 * (sunMeanLong - rahuTropicalDeg) * rad)
                       - 0.1500 * Math.sin((357.5291 + 0.98560028 * j2000Days) * rad);
    rahuTropicalDeg = (rahuTropicalDeg + correction + 360) % 360;
  }
  const rahuSidereal = (rahuTropicalDeg - ayanamsha + 360) % 360;
  const ketuSidereal = (rahuSidereal + 180) % 360;

  // Check planetary motion speed for retrograding
  const deltaT = 0.01; // 15 mins step
  const timePlus = Astronomy.MakeTime(new Date(utcDateMs + 900000));
  const sunPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, timePlus, true));
  const marsPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Mars, timePlus, true));
  const mercuryPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Mercury, timePlus, true));
  const jupiterPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Jupiter, timePlus, true));
  const venusPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Venus, timePlus, true));
  const saturnPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Saturn, timePlus, true));

  // Daily motion rate calculation for the 5 Tara Grahas (degrees/day)
  const timeMinus12h = Astronomy.MakeTime(new Date(utcDateMs - 43200000));
  const timePlus12h = Astronomy.MakeTime(new Date(utcDateMs + 43200000));

  const getPlanetDailyRate = (body: any): number => {
    const p0 = Astronomy.Ecliptic(Astronomy.GeoVector(body, timeMinus12h, true));
    const p1 = Astronomy.Ecliptic(Astronomy.GeoVector(body, timePlus12h, true));
    let diff = p1.elon - p0.elon;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return diff; // Degrees per 24 hours
  };

  const taraBodies: Partial<Record<PlanetName, any>> = {
    Mars: Astronomy.Body.Mars,
    Mercury: Astronomy.Body.Mercury,
    Jupiter: Astronomy.Body.Jupiter,
    Venus: Astronomy.Body.Venus,
    Saturn: Astronomy.Body.Saturn
  };

  const isRetrograde = (current: number, future: number) => {
    let diff = future - current;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return diff < 0;
  };

  const ascendantDeg = calculateAscendant(birthUtcDate, details.latitude, details.longitude, ayanamsha);

  // Convert to Sidereal Longitudes
  const siderealMap: { [key in PlanetName]: { lon: number; retro: boolean } } = {
    Ascendant: { lon: ascendantDeg, retro: false },
    Sun: { lon: (sunEcl.elon - ayanamsha + 360) % 360, retro: false },
    Moon: { lon: (moonEcl.elon - ayanamsha + 360) % 360, retro: false },
    Mars: { lon: (marsEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(marsEcl.elon, marsPlus.elon) },
    Mercury: { lon: (mercuryEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(mercuryEcl.elon, mercuryPlus.elon) },
    Jupiter: { lon: (jupiterEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(jupiterEcl.elon, jupiterPlus.elon) },
    Venus: { lon: (venusEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(venusEcl.elon, venusPlus.elon) },
    Saturn: { lon: (saturnEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(saturnEcl.elon, saturnPlus.elon) },
    Rahu: { lon: rahuSidereal, retro: true },
    Ketu: { lon: ketuSidereal, retro: true }
  };

  const ascSignIndex = Math.floor(ascendantDeg / 30);

  // Build PlanetPosition objects
  const planetNames: PlanetName[] = ['Ascendant', 'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

  // Preliminary house mapping for compound friendship evaluation
  const preliminaryHouses: { name: PlanetName; house: number }[] = planetNames.map((name) => {
    const lon = siderealMap[name].lon;
    const signIndex = Math.floor(lon / 30);
    const house = ((signIndex - ascSignIndex + 12) % 12) + 1;
    return { name, house };
  });

  const planets: PlanetPosition[] = planetNames.map((name) => {
    const lon = siderealMap[name].lon;
    const signIndex = Math.floor(lon / 30);
    const sign = ZODIAC_SIGNS[signIndex];
    const signLord = ZODIAC_LORDS[sign];
    const degreeInSign = lon % 30;

    // House calculation relative to Ascendant sign (Whole Sign / Equal House system)
    const house = ((signIndex - ascSignIndex + 12) % 12) + 1;

    // Nakshatra calculation (27 nakshatras, 13.3333 degrees each)
    const nakshatraIndex = Math.floor(lon / (360 / 27));
    const nakshatraObj = NAKSHATRAS[nakshatraIndex];
    const degInNakshatra = lon % (360 / 27);
    const pada = Math.floor(degInNakshatra / (360 / 108)) + 1; // 1 to 4

    // Combust status (within ~12 deg of Sun, except Sun & Nodes)
    const sunLon = siderealMap['Sun'].lon;
    let distToSun = Math.abs(lon - sunLon);
    if (distToSun > 180) distToSun = 360 - distToSun;
    const isCombust = name !== 'Sun' && name !== 'Ascendant' && name !== 'Rahu' && name !== 'Ketu' && distToSun < 12;

    // Dignity estimation using authentic 9-tier Panchadha Maitri hierarchy
    const dignity = calculateDignity(name, signIndex, degreeInSign, house, preliminaryHouses);

    // Parashari Special Aspects (Drishtis)
    const drishtiHouses = getSpecialDrishtiHouses(name, house);

    // Ashtavidha Motional State calculation for Tara Grahas (BPHS Ch. 27 / Surya Siddhanta)
    let dailyMotion: number | undefined;
    let ashtavidhaState: 'Vakra' | 'Anuvakra' | 'Vikala' | 'Mandatara' | 'Manda' | 'Sama' | 'Chara' | 'Atichara' | undefined;
    let ashtavidhaReason: string | undefined;

    const taraBody = taraBodies[name];
    if (taraBody) {
      dailyMotion = getPlanetDailyRate(taraBody);
      const classification = classifyAshtavidhaMotion(name, dailyMotion);
      ashtavidhaState = classification.state;
      ashtavidhaReason = classification.reason;
    }

    return {
      name,
      symbol: PLANET_SYMBOLS[name],
      longitude: lon,
      sign,
      signIndex,
      signLord,
      degreeInSign,
      degreeFormatted: formatDegree(degreeInSign),
      house,
      nakshatra: nakshatraObj.name,
      nakshatraIndex,
      nakshatraLord: nakshatraObj.lord,
      pada,
      isRetrograde: siderealMap[name].retro,
      isCombust,
      speed: dailyMotion,
      dailyMotion,
      ashtavidhaState,
      ashtavidhaReason,
      dignity,
      drishtiHouses
    };
  });

  // Calculate Sripati Bhava Sphutas
  const sripati = calculateSripatiBhavaSphutas(birthUtcDate, details.latitude, details.longitude, ayanamsha, ascendantDeg);

  // HouseInfo array: Canonical Whole-Sign Rashi boundaries with Sripati Bhava Madhyas, Sandhis & Spans
  const houses: HouseInfo[] = Array.from({ length: 12 }, (_, i) => {
    const houseNumber = i + 1;
    const signIndex = (ascSignIndex + i) % 12;
    const sign = ZODIAC_SIGNS[signIndex];
    const signLord = ZODIAC_LORDS[sign];
    const planetsInHouse = planets
      .filter((p) => p.house === houseNumber)
      .map((p) => p.name);

    const degreeStart = signIndex * 30;
    const bhavaMadhya = ((ascendantDeg + i * 30) % 360 + 360) % 360;

    return {
      houseNumber,
      sign,
      signIndex,
      signLord,
      degreeStart,
      degreeFormatted: formatDegree(degreeStart),
      bhavaMadhya,
      bhavaMadhyaFormatted: formatDegree(bhavaMadhya),
      bhavaArambha: sripati.arambhas[i],
      bhavaArambhaFormatted: formatDegree(sripati.arambhas[i]),
      bhavaAnta: sripati.antas[i],
      bhavaAntaFormatted: formatDegree(sripati.antas[i]),
      bhavaSpan: sripati.spans[i],
      planetsInHouse
    };
  });

  // Calculate Panchang
  const sunLon = siderealMap['Sun'].lon;
  const moonLon = siderealMap['Moon'].lon;
  const panchang = calculatePanchang(sunLon, moonLon, birthUtcDate, ayanamsha, details.latitude, details.longitude, tzOffset);

  // Avakhada Chakra
  const avakhada = calculateAvakhada(moonLon);

  // Divisional Varga Charts (Shodashvarga: D1 to D60) + Chandra, Surya, Transit & Chalit
  const charts = {
    D1: buildChartData('D1', 'Lagna Kundali', planets, ascSignIndex),
    Chandra: buildChandraChart(planets),
    Surya: buildSuryaChart(planets),
    Transit: buildTransitChart(planets, ascSignIndex, ascendantDeg, new Date(), convention),
    Chalit: buildChalitChart(planets, sripati, ascSignIndex),
    D2: buildHoraChart(planets, ascSignIndex),
    D3: buildDrekkanaChart(planets, ascSignIndex),
    D4: buildChaturthamshaChart(planets, ascSignIndex),
    D7: buildSaptamshaChart(planets, ascSignIndex),
    D9: buildNavamshaChart(planets, ascSignIndex),
    D10: buildDashamshaChart(planets, ascSignIndex),
    D12: buildDwadasamshaChart(planets, ascSignIndex),
    D16: buildShodashamshaChart(planets, ascSignIndex),
    D20: buildVimshamshaChart(planets, ascSignIndex),
    D24: buildChaturvimshamshaChart(planets, ascSignIndex),
    D27: buildSaptavimshamshaChart(planets, ascSignIndex),
    D30: buildTrishamshaChart(planets, ascSignIndex),
    D40: buildKhavedamshaChart(planets, ascSignIndex),
    D45: buildAkshavedamshaChart(planets, ascSignIndex),
    D60: buildShashtiamshaChart(planets, ascSignIndex)
  };

  // Vimshottari Dashas
  const dashas = calculateVimshottariDasha(moonLon, birthUtcDate);

  // Gemstones & Remedies
  const gemstones = getGemstoneRecommendations(ascSignIndex, planets, dashas);

  // Ashtakvarga (Canonical Sign-Indexed BAV/SAV & Derived Bhava Views)
  const ashtakvarga = calculateAshtakvarga(planets, ascSignIndex);

  // Parashari Strengths: Shadbala & Bhava Bala
  const shadbala = calculateShadbala(planets, houses, {
    birthDate: birthUtcDate,
    latitude: details.latitude,
    longitude: details.longitude,
    panchang,
    charts,
    ayanamshaDegrees: ayanamsha
  });

  // Layer 8 Authoritative Canonical Yogas & Doshas Evaluation
  const partialKundali = {
    planets,
    houses,
    charts,
    dashas,
    ashtakvarga,
    shadbala
  } as unknown as CompleteKundali;
  const yogaEvaluation = evaluateAllYogasAndDoshas(partialKundali);
  const yogas = yogaEvaluation.yogas;
  const manglik = yogaEvaluation.manglik;
  const kaalSarp = yogaEvaluation.kaalSarp;

  // Doshas & Transits
  const liveEphemeris = calculateLivePlanetaryEphemeris(new Date());
  const transitSaturn = liveEphemeris.find(p => p.planet === 'Saturn');
  const sadeSati = calculateSadeSati(planets, transitSaturn, birthUtcDate, dashas, new Date(), ayanamshaSystem);
  const pitraDosha = calculatePitraDosha(planets);

  // Jaimini Chara Karakas & Planetary Avasthas
  const charaKarakas = calculateCharaKarakas(planets);
  const avasthas = calculateAvasthas(planets);

  // Live Planetary Gochar (Transits) using identical node convention
  const gochar = calculateGocharTransits(planets, new Date(), convention);

  // Calculation Provenance Metadata
  const calculationProvenance: CalculationProvenance = {
    engine: 'VedangJyotisham-Canonical-Vedic-Core',
    version: '6.4.0',
    ephemeris: 'Astronomy Engine (IAU SOFA compliant)',
    ayanamshaSystem,
    ayanamshaDegrees: ayanamsha,
    nodeConvention: convention,
    obliquityModel: 'IAU true obliquity of date (e_tilt)',
    dashaYearBasis: 'Sauramana (365.25 days solar year)',
    shadbalaMethodology: 'BPHS Ch. 27-29 Classical Parashari (Method A Ashtavidha)',
    ashtakavargaMethodology: 'BPHS Ch. 66-72 Classical Parashari (337 Bindu Invariant, Trikona & Ekadhipatya Shodhana, Shodhya Pinda)',
    yogaDoshaMethodology: 'BPHS Ch. 35-41, Phaladeepika Ch. 6, Saravali Classical Parashari (32 Nabhasa, 5 Mahapurusha, Raja/Dhana, Manglik & Kaal Sarp)',
    destinySynthesisMethodology: 'Parashari Dasha-Bhukti Horizon Synthesis, 5-Level Vimshottari Progression, Transit Ephemeris & Canonical Panchanga Timing',
    muhurtaMethodology: 'Kalaprakasika, Muhurta Chintamani & Dharma Sindhu Classical Electional Engine (Panchanga Shuddhi, Navatara Chakra, Ashtama Shuddhi, Gandanta, & 16 Samskaras)',
    vastuMethodology: 'Brihat Samhita, Mayamata, Manasara & Classical 16-Zone MahaVastu Spatial Geometry (Astro-Vastu Graha-Bhava Alignment)',
    synastryMethodology: 'Brihat Parashara Hora Shastra (Ch. 57) & Muhurta Chintamani Classical 36-Guna Ashtakoota & Manglik Parihara Engine',
    numerologyMethodology: 'Vedic Anka Shastra (Moolank/Bhagyank), Classical Chaldean Cheiro System (1-8 Name Vibrations), Pythagorean (1-9), and Chinese Lo Shu Magic Square & Ba Zhai Kua',
    gemstoneMethodology: 'Brihat Samhita (Ratna Pariksha), Garuda Purana, Jataka Parijata (Ch. 2), & Classical Parashari Anukul Ratna Shastra (Functional Beneficence, Tara Bala, & Navamsha Confirmation)',
    remediesMethodology: 'Brihat Parashara Hora Shastra (Ch. 84-97 Upaya Adhyaya), Brihat Samhita, Garuda Purana, & Classical Sattvic Daiva-Vyapashraya / Yukti-Vyapashraya Karmic Balancing',
    calculatedAtUtc: new Date().toISOString()
  };

  // Canonical Sacred Dignities (Vargottama, Pushkaramsha, Pushkara Bhaga)
  const d9Chart = charts.D9;
  const enrichedPlanets: PlanetPosition[] = planets.map((p) => {
    if (p.name === 'Ascendant') {
      const d9Asc = d9Chart.ascendantIndex;
      return {
        ...p,
        isVargottama: isVargottama(ascSignIndex, d9Asc),
        isPushkarNavamsha: isPushkarNavamsha(ascSignIndex, d9Asc),
        isPushkarBhaga: isPushkarBhaga(ascSignIndex, p.degreeInSign)
      };
    }
    const d9Pos = d9Chart.planets.find((item) => item.name === p.name);
    const d9Sign = d9Pos ? d9Pos.signIndex : p.signIndex;
    return {
      ...p,
      isVargottama: isVargottama(p.signIndex, d9Sign),
      isPushkarNavamsha: isPushkarNavamsha(p.signIndex, d9Sign),
      isPushkarBhaga: isPushkarBhaga(p.signIndex, p.degreeInSign)
    };
  });

  // Parashari Varga Bhedas (Parijata to Airavata Dignity Counts across Shodashvarga)
  const vargaBhedas = calculateVargaBhedas(charts, enrichedPlanets);
  const finalCharts = {
    ...charts,
    D1: buildChartData('D1', 'Lagna Kundali', enrichedPlanets, ascSignIndex)
  };

  return {
    nodeConvention: convention,
    birthDetails: {
      ...details,
      nodeConvention: convention
    },
    panchang,
    avakhada,
    planets: enrichedPlanets,
    houses,
    charts: finalCharts,
    dashas,
    yogas,
    manglik,
    kaalSarp,
    sadeSati,
    pitraDosha,
    gemstones,
    ashtakvarga,
    shadbala,
    charaKarakas,
    avasthas,
    gochar,
    doubleTransit: calculateDoubleTransit(enrichedPlanets, new Date(), convention),
    doubleTransitPeriods: calculateDoubleTransitPeriods(ascendantDeg, ayanamsha, ayanamshaSystem),
    vargaBhedas,
    charaDasha: calculateCanonicalCharaDasha({
      houses,
      planets: enrichedPlanets,
      birthDetails: details
    }, new Date()),
    calculationProvenance,
    calculatedAt: new Date().toISOString()
  };
}

/**
 * Calculates Panchang elements using authoritative canonical Panchanga algorithms
 */
function calculatePanchang(
  sunLon: number, 
  moonLon: number, 
  birthDate: Date, 
  ayanamsha: number,
  latitude: number = 28.6139,
  longitude: number = 77.2090,
  timezoneOffsetMinutes: number = 330
): PanchangInfo {
  const tithi = calculateCanonicalTithi(sunLon, moonLon);
  const nakshatra = calculateCanonicalNakshatra(moonLon);
  const yoga = calculateCanonicalYoga(sunLon, moonLon);
  const karana = calculateCanonicalKarana(sunLon, moonLon);
  const sunTimes = calculateAuthoritativeSunTimes(birthDate, latitude, longitude);
  const vara = calculateCanonicalVara(birthDate, sunTimes.sunrise, timezoneOffsetMinutes);

  return {
    tithi: tithi.name,
    tithiNumber: tithi.number,
    vara: vara.nameEn,
    nakshatra: nakshatra.name,
    yoga: yoga.name,
    karana: karana.name,
    paksha: tithi.paksha,
    sunrise: formatTime(sunTimes.sunrise, false, undefined, timezoneOffsetMinutes),
    sunset: formatTime(sunTimes.sunset, false, undefined, timezoneOffsetMinutes),
    ayanamsha,
    ayanamshaFormatted: formatDegree(ayanamsha)
  };
}

/**
 * Calculates Avakhada Chakra
 */
function calculateAvakhada(moonLon: number): AvakhadaChakra {
  const nakIdx = Math.floor(moonLon / (360 / 27));
  const nakObj = NAKSHATRAS[nakIdx];
  const signIdx = Math.floor(moonLon / 30);
  const signLord = ZODIAC_LORDS[ZODIAC_SIGNS[signIdx]];

  const VARNAS = ['Brahmin', 'Kshatriya', 'Vaishya', 'Shudra'];
  const GANAS = ['Deva', 'Manushya', 'Rakshasa'];
  const NADIS = ['Adi', 'Madhya', 'Antya'];
  const TATVAS = ['Fire', 'Earth', 'Air', 'Water'];

  return {
    varna: VARNAS[signIdx % 4],
    vashya: 'Chatushpada / Nara',
    yoni: 'Gaja / Ashwa / Simha',
    gana: GANAS[nakIdx % 3],
    nadi: NADIS[nakIdx % 3],
    signLord,
    nakshatraLord: nakObj.lord,
    tatva: TATVAS[signIdx % 4],
    paya: (['Silver', 'Gold', 'Copper', 'Iron'] as const)[signIdx % 4]
  };
}

// Classical Moolatrikona Definitions
const MOOLATRIKONA: Partial<Record<PlanetName, { signIndex: number; startDeg: number; endDeg: number }>> = {
  Sun: { signIndex: 4, startDeg: 0, endDeg: 20 },         // Leo 0-20°
  Moon: { signIndex: 1, startDeg: 3, endDeg: 30 },        // Taurus 3-30°
  Mars: { signIndex: 0, startDeg: 0, endDeg: 12 },        // Aries 0-12°
  Mercury: { signIndex: 5, startDeg: 15, endDeg: 20 },     // Virgo 15-20°
  Jupiter: { signIndex: 8, startDeg: 0, endDeg: 10 },     // Sagittarius 0-10°
  Venus: { signIndex: 6, startDeg: 0, endDeg: 15 },       // Libra 0-15°
  Saturn: { signIndex: 10, startDeg: 0, endDeg: 20 }      // Aquarius 0-20°
};

/**
 * Calculates authentic Parashari planetary dignity (9-tier hierarchy)
 * Incorporates Exaltation, Debilitation, Moolatrikona, Swakshetra (Own House),
 * and Panchadha Maitri (Compound Friendship: Naisargika + Tatkalika) with Dispositor.
 */
export function calculateDignity(
  planet: PlanetName,
  signIndex: number,
  deg: number,
  house?: number,
  allPlanets?: { name: PlanetName; house: number }[]
): PlanetPosition['dignity'] {
  if (planet === 'Ascendant') return 'Neutral';

  // Exaltation and Debilitation Signs
  const EXALTATIONS: { [key in PlanetName]?: number } = {
    Sun: 0, // Aries
    Moon: 1, // Taurus
    Mars: 9, // Capricorn
    Mercury: 5, // Virgo
    Jupiter: 3, // Cancer
    Venus: 11, // Pisces
    Saturn: 6, // Libra
    Rahu: 1, // Taurus
    Ketu: 7 // Scorpio
  };

  const DEBILITATIONS: { [key in PlanetName]?: number } = {
    Sun: 6, // Libra
    Moon: 7, // Scorpio
    Mars: 3, // Cancer
    Mercury: 11, // Pisces
    Jupiter: 9, // Capricorn
    Venus: 5, // Virgo
    Saturn: 0, // Aries
    Rahu: 7, // Scorpio
    Ketu: 1 // Taurus
  };

  if (EXALTATIONS[planet] === signIndex) return 'Exalted';
  if (DEBILITATIONS[planet] === signIndex) return 'Debilitated';

  const mt = MOOLATRIKONA[planet];
  if (mt && mt.signIndex === signIndex && deg >= mt.startDeg && deg <= mt.endDeg) {
    return 'Moolatrikona';
  }

  const ownLord = ZODIAC_LORDS[ZODIAC_SIGNS[signIndex]];
  if (ownLord === planet) return 'Own House';

  // Dispositor relationship evaluation via Panchadha Maitri
  const dispositor = (ownLord || 'Sun') as PlanetName;
  const natural = NATURAL_FRIENDSHIPS[planet];
  const natRel: 'Friend' | 'Neutral' | 'Enemy' = !natural
    ? 'Neutral'
    : natural.friends.includes(dispositor)
    ? 'Friend'
    : natural.enemies.includes(dispositor)
    ? 'Enemy'
    : 'Neutral';

  // Temporal relationship: planets in 2, 3, 4, 10, 11, 12 from current planet are temporal friends
  let tempRel: 'Friend' | 'Enemy' = 'Enemy';
  if (house && allPlanets) {
    const dispPlanet = allPlanets.find(p => p.name === dispositor);
    if (dispPlanet) {
      const houseDiff = ((dispPlanet.house - house + 12) % 12) + 1;
      if ([2, 3, 4, 10, 11, 12].includes(houseDiff)) {
        tempRel = 'Friend';
      }
    }
  }

  return getPanchadhaMaitriCompound(natRel, tempRel);
}

/**
 * Vimshottari Dasha Engine — Single Authoritative Core
 * Calculates exact starting Nakshatra balance at birth, zero pre-birth active dates,
 * and strict hierarchical boundary consistency with 365.25 days/year standard.
 */
export function calculateVimshottariDasha(moonLon: number, birthDate: Date): DashaPeriod[] {
  const nakIdx = Math.floor(moonLon / (360 / 27));
  const degInNak = moonLon % (360 / 27);
  const fracPassed = degInNak / (360 / 27);
  const remainingFraction = Math.max(0, Math.min(1, 1 - fracPassed));

  // Starting Dasha lord index based on Moon's Nakshatra
  const startLordIndex = nakIdx % 9;
  const firstPlanetOrder = DASHA_ORDER[startLordIndex];
  
  const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
  const birthMs = birthDate.getTime();
  const nowMs = Date.now();

  const dashaPeriods: DashaPeriod[] = [];
  let currentStartMs = birthMs;

  // Generate 2 full cycles (18 periods = 240 years) to ensure all ages & future years are covered
  for (let i = 0; i < 18; i++) {
    const idx = (startLordIndex + i) % 9;
    const dashaObj = DASHA_ORDER[idx];
    
    // Initial Mahadasha at birth takes only the remaining balance
    const durationYears = (i === 0) 
      ? firstPlanetOrder.years * remainingFraction 
      : dashaObj.years;
    
    const durationMs = Math.round(durationYears * MS_PER_YEAR);
    const mdStartMs = currentStartMs;
    const mdEndMs = mdStartMs + durationMs;
    const isCurrent = nowMs >= mdStartMs && nowMs < mdEndMs;

    const startDateStr = new Date(mdStartMs).toISOString().split('T')[0];
    const endDateStr = new Date(mdEndMs).toISOString().split('T')[0];

    // Antardashas (Sub-periods)
    const antardashas: DashaPeriod[] = [];

    if (i === 0) {
      // For the first Mahadasha at birth:
      // Nominal cycle started theoretically at (birthMs - elapsedYears * MS_PER_YEAR)
      const elapsedYears = firstPlanetOrder.years * fracPassed;
      const theoCycleStartMs = birthMs - (elapsedYears * MS_PER_YEAR);
      let runningTheoMs = theoCycleStartMs;

      for (let j = 0; j < 9; j++) {
        const subIdx = (startLordIndex + j) % 9;
        const subDashaObj = DASHA_ORDER[subIdx];
        const nominalAdYears = (firstPlanetOrder.years * subDashaObj.years) / 120;
        const nominalAdMs = Math.round(nominalAdYears * MS_PER_YEAR);
        
        const adTheoStartMs = runningTheoMs;
        const adTheoEndMs = (j === 8) ? mdEndMs : Math.min(mdEndMs, adTheoStartMs + nominalAdMs);
        runningTheoMs = adTheoEndMs;

        // Skip Antardashas that concluded prior to birth
        if (adTheoEndMs <= birthMs) {
          continue;
        }

        // For the active Antardasha at birth, clamp start to birthMs
        const adEffectiveStartMs = Math.max(birthMs, adTheoStartMs);
        const adEffectiveEndMs = Math.min(mdEndMs, adTheoEndMs);
        const adDurationYears = (adEffectiveEndMs - adEffectiveStartMs) / MS_PER_YEAR;

        const subIsCurrent = nowMs >= adEffectiveStartMs && nowMs < adEffectiveEndMs;

        // Pratyantardashas (Level 3 Sub-sub-periods) for this initial Antardasha
        const pratyantardashas: DashaPeriod[] = [];
        let runningPdTheoMs = adTheoStartMs;

        for (let k = 0; k < 9; k++) {
          const pdIdx = (subIdx + k) % 9;
          const pdDashaObj = DASHA_ORDER[pdIdx];
          const nominalPdYears = (nominalAdYears * pdDashaObj.years) / 120;
          const nominalPdMs = Math.round(nominalPdYears * MS_PER_YEAR);

          const pdTheoStartMs = runningPdTheoMs;
          const pdTheoEndMs = (k === 8) ? adTheoEndMs : Math.min(adTheoEndMs, pdTheoStartMs + nominalPdMs);
          runningPdTheoMs = pdTheoEndMs;

          if (pdTheoEndMs <= birthMs) {
            continue;
          }

          const pdEffectiveStartMs = Math.max(birthMs, pdTheoStartMs);
          const pdEffectiveEndMs = Math.min(adEffectiveEndMs, pdTheoEndMs);
          const pdDurationYears = (pdEffectiveEndMs - pdEffectiveStartMs) / MS_PER_YEAR;
          const pdIsCurrent = nowMs >= pdEffectiveStartMs && nowMs < pdEffectiveEndMs;

          pratyantardashas.push({
            planet: pdDashaObj.planet,
            startDate: new Date(pdEffectiveStartMs).toISOString().split('T')[0],
            endDate: new Date(pdEffectiveEndMs).toISOString().split('T')[0],
            durationYears: Number(pdDurationYears.toFixed(2)),
            isCurrent: pdIsCurrent
          });
        }

        antardashas.push({
          planet: subDashaObj.planet,
          startDate: new Date(adEffectiveStartMs).toISOString().split('T')[0],
          endDate: new Date(adEffectiveEndMs).toISOString().split('T')[0],
          durationYears: Number(adDurationYears.toFixed(2)),
          isCurrent: subIsCurrent,
          pratyantardashas,
          antardashas: pratyantardashas
        });
      }
    } else {
      // For subsequent full Mahadashas:
      let subCurrentMs = mdStartMs;

      for (let j = 0; j < 9; j++) {
        const subIdx = (idx + j) % 9;
        const subDashaObj = DASHA_ORDER[subIdx];
        const subDurationYears = (dashaObj.years * subDashaObj.years) / 120;
        const subDurationMs = Math.round(subDurationYears * MS_PER_YEAR);
        
        const adStartMs = subCurrentMs;
        const adEndMs = (j === 8) ? mdEndMs : Math.min(mdEndMs, adStartMs + subDurationMs);
        const subIsCurrent = nowMs >= adStartMs && nowMs < adEndMs;

        // Pratyantardashas (Level 3 Sub-sub-periods) for this full Antardasha
        const pratyantardashas: DashaPeriod[] = [];
        let pdCurrentMs = adStartMs;

        for (let k = 0; k < 9; k++) {
          const pdIdx = (subIdx + k) % 9;
          const pdDashaObj = DASHA_ORDER[pdIdx];
          const pdDurationYears = (subDurationYears * pdDashaObj.years) / 120;
          const pdDurationMs = Math.round(pdDurationYears * MS_PER_YEAR);

          const pdStartMs = pdCurrentMs;
          const pdEndMs = (k === 8) ? adEndMs : Math.min(adEndMs, pdStartMs + pdDurationMs);
          const pdIsCurrent = nowMs >= pdStartMs && nowMs < pdEndMs;

          pratyantardashas.push({
            planet: pdDashaObj.planet,
            startDate: new Date(pdStartMs).toISOString().split('T')[0],
            endDate: new Date(pdEndMs).toISOString().split('T')[0],
            durationYears: Number(pdDurationYears.toFixed(2)),
            isCurrent: pdIsCurrent
          });

          pdCurrentMs = pdEndMs;
        }

        antardashas.push({
          planet: subDashaObj.planet,
          startDate: new Date(adStartMs).toISOString().split('T')[0],
          endDate: new Date(adEndMs).toISOString().split('T')[0],
          durationYears: Number(subDurationYears.toFixed(2)),
          isCurrent: subIsCurrent,
          pratyantardashas,
          antardashas: pratyantardashas
        });

        subCurrentMs = adEndMs;
      }
    }

    dashaPeriods.push({
      planet: dashaObj.planet,
      startDate: startDateStr,
      endDate: endDateStr,
      durationYears: Number(durationYears.toFixed(2)),
      isCurrent,
      antardashas
    });

    currentStartMs = mdEndMs;

    // Stop if we have generated at least 9 periods AND end date is well past current date
    if (i >= 8 && mdEndMs > nowMs + 30 * MS_PER_YEAR) {
      break;
    }
  }

  return dashaPeriods;
}

/**
 * Canonical Jaimini Chara Dasha Engine (Jaimini Upadesha Sutras)
 * - Savya (Direct) signs: Aries, Taurus, Gemini, Libra, Scorpio, Sagittarius (forward progression and forward counting).
 * - Apasavya (Indirect) signs: Cancer, Leo, Virgo, Capricorn, Aquarius, Pisces (reverse progression and reverse counting).
 * - Exaltation (+1 yr) and Debilitation (-1 yr) adjustments.
 * - Dual lordships for Scorpio (Mars/Ketu) and Aquarius (Saturn/Rahu).
 */
export function calculateCanonicalCharaDasha(
  kundali: Partial<CompleteKundali> | CompleteKundali,
  targetDate: Date = new Date()
): CharaDashaData {
  const lagnaHouse = kundali.houses?.[0];
  const lagnaSign = (lagnaHouse?.sign || 'Aries') as ZodiacSign;
  const lagnaIdx = ZODIAC_SIGNS.indexOf(lagnaSign);
  const safeLagnaIdx = lagnaIdx >= 0 ? lagnaIdx : 0;

  // Savya (Direct): Aries (0), Taurus (1), Gemini (2), Libra (6), Scorpio (7), Sagittarius (8)
  const savyaSigns: ZodiacSign[] = ['Aries', 'Taurus', 'Gemini', 'Libra', 'Scorpio', 'Sagittarius'];
  const isDirect = savyaSigns.includes(lagnaSign);

  const planets = kundali.planets || [];

  // Helper to find planet's house (1-12)
  const getHouseOfPlanet = (planetName: PlanetName): number => {
    const pl = planets.find(p => p.name === planetName);
    return pl ? pl.house : 1;
  };

  // Helper to check planet dignity
  const getPlanetDignity = (planetName: PlanetName): string => {
    const pl = planets.find(p => p.name === planetName);
    return pl ? pl.dignity : 'Neutral';
  };

  const sequence: CharaDashaPeriod[] = [];
  let currentAge = 0;

  for (let i = 0; i < 12; i++) {
    const signIdx = isDirect
      ? (safeLagnaIdx + i) % 12
      : ((safeLagnaIdx - i + 120) % 12);
    const sign = ZODIAC_SIGNS[signIdx];
    const isSignSavya = savyaSigns.includes(sign);

    // Resolve Sign Lord (including dual lordships for Scorpio and Aquarius)
    let signLord: PlanetName = ZODIAC_LORDS[sign] as PlanetName;
    if (sign === 'Scorpio') {
      const marsHouse = getHouseOfPlanet('Mars');
      const ketuHouse = getHouseOfPlanet('Ketu');
      const marsWith = planets.filter(p => p.house === marsHouse && p.name !== 'Mars').length;
      const ketuWith = planets.filter(p => p.house === ketuHouse && p.name !== 'Ketu').length;
      signLord = ketuWith > marsWith ? 'Ketu' : 'Mars';
    } else if (sign === 'Aquarius') {
      const saturnHouse = getHouseOfPlanet('Saturn');
      const rahuHouse = getHouseOfPlanet('Rahu');
      const satWith = planets.filter(p => p.house === saturnHouse && p.name !== 'Saturn').length;
      const rahuWith = planets.filter(p => p.house === rahuHouse && p.name !== 'Rahu').length;
      signLord = rahuWith > satWith ? 'Rahu' : 'Saturn';
    }

    const lordHouse = getHouseOfPlanet(signLord);
    const signHouseFromLagna = ((signIdx - safeLagnaIdx + 12) % 12) + 1;

    let dist: number;
    if (isSignSavya) {
      // Forward counting from sign to lord
      dist = ((lordHouse - signHouseFromLagna + 12) % 12);
    } else {
      // Backward/reverse counting from sign to lord
      dist = ((signHouseFromLagna - lordHouse + 12) % 12);
    }

    // Lord in own sign gives 12 years
    let years = dist === 0 ? 12 : dist;

    // Classical Exaltation (+1) and Debilitation (-1) adjustments
    const dignity = getPlanetDignity(signLord);
    if (dignity === 'Exalted' && years < 12) {
      years += 1;
    } else if (dignity === 'Debilitated' && years > 1) {
      years -= 1;
    }

    years = Math.max(1, Math.min(12, years));

    sequence.push({
      sign,
      years,
      startAge: currentAge,
      endAge: currentAge + years
    });
    currentAge += years;
  }

  // Calculate dates based on birthDate
  const birthDateStr = (kundali.birthDetails as any)?.date;
  const birthTimeMs = birthDateStr ? new Date(birthDateStr).getTime() : 0;
  const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

  if (birthTimeMs > 0) {
    let runningStartMs = birthTimeMs;
    for (const item of sequence) {
      const durMs = Math.round(item.years * MS_PER_YEAR);
      item.startDate = new Date(runningStartMs).toISOString().split('T')[0];
      item.endDate = new Date(runningStartMs + durMs).toISOString().split('T')[0];
      runningStartMs += durMs;
    }
  }

  const ageYears = birthTimeMs > 0 
    ? Math.max(0, (targetDate.getTime() - birthTimeMs) / MS_PER_YEAR) 
    : 0;
  const cycleAge = currentAge > 0 ? ageYears % currentAge : 0;

  const activePeriod = sequence.find(s => cycleAge >= s.startAge && cycleAge < s.endAge) || sequence[0];
  if (activePeriod) {
    activePeriod.isCurrent = true;
  }

  return {
    activeSign: activePeriod ? activePeriod.sign : sequence[0].sign,
    sequence
  };
}

/**
 * Builds generic Chart object
 */
function buildChartData(code: string, title: string, planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const houses: HouseInfo[] = Array.from({ length: 12 }, (_, i) => {
    const houseNumber = i + 1;
    const signIndex = (ascSignIndex + i) % 12;
    const sign = ZODIAC_SIGNS[signIndex];
    const signLord = ZODIAC_LORDS[sign];
    const planetsInHouse = planets
      .filter((p) => p.house === houseNumber)
      .map((p) => p.name);

    const degreeStart = signIndex * 30;
    return {
      houseNumber,
      sign,
      signIndex,
      signLord,
      degreeStart,
      degreeFormatted: formatDegree(degreeStart),
      bhavaMadhya: degreeStart + 15,
      bhavaMadhyaFormatted: formatDegree(degreeStart + 15),
      planetsInHouse
    };
  });

  return {
    divisionalCode: code,
    title,
    houses,
    planets,
    ascendantIndex: ascSignIndex
  };
}

/**
 * Chandra Chart (Moon Lagna Chart) Builder
 */
function buildChandraChart(planets: PlanetPosition[]): KundaliChartData {
  const moonObj = planets.find((p) => p.name === 'Moon')!;
  const moonSignIdx = moonObj.signIndex;

  const chandraPlanets = planets.map((p) => {
    const house = ((p.signIndex - moonSignIdx + 12) % 12) + 1;
    return {
      ...p,
      house
    };
  });

  return buildChartData('Chandra', 'Chandra Kundali', chandraPlanets, moonSignIdx);
}

/**
 * Surya Chart (Sun Lagna Chart) Builder
 */
function buildSuryaChart(planets: PlanetPosition[]): KundaliChartData {
  const sunObj = planets.find((p) => p.name === 'Sun')!;
  const sunSignIdx = sunObj.signIndex;

  const suryaPlanets = planets.map((p) => {
    const house = ((p.signIndex - sunSignIdx + 12) % 12) + 1;
    return {
      ...p,
      house
    };
  });

  return buildChartData('Surya', 'Surya Kundali', suryaPlanets, sunSignIdx);
}

/**
 * Transit Chart (Gochar Chart) Builder: Plotted against Natal Lagna
 */
function buildTransitChart(
  planets: PlanetPosition[],
  ascSignIndex: number,
  ascendantDeg?: number,
  transitDate: Date = new Date(),
  context?: CalculationContext | NodeConvention
): KundaliChartData {
  const liveEphem = calculateLivePlanetaryEphemeris(transitDate, context);

  const sunPos = liveEphem.find((p) => p.planet === 'Sun');
  const sunLon = sunPos ? sunPos.longitude : 0;

  const transitPlanets: PlanetPosition[] = liveEphem.map((p) => {
    const house = ((p.signIndex - ascSignIndex + 12) % 12) + 1;
    const nakIdx = Math.floor(p.longitude / (360 / 27));
    const nakObj = NAKSHATRAS[nakIdx] || { name: 'Ashwini', lord: 'Ketu' };
    const degInNak = p.longitude % (360 / 27);
    const pada = Math.floor(degInNak / (360 / 108)) + 1;

    let distToSun = Math.abs(p.longitude - sunLon);
    if (distToSun > 180) distToSun = 360 - distToSun;
    const isCombust = p.planet !== 'Sun' && p.planet !== 'Ascendant' && p.planet !== 'Rahu' && p.planet !== 'Ketu' && distToSun < 12;

    const dignity = calculateDignity(p.planet, p.signIndex, p.degreeInSign, house);

    return {
      name: p.planet,
      symbol: PLANET_SYMBOLS[p.planet] || p.planet.slice(0, 2),
      longitude: p.longitude,
      sign: p.sign,
      signIndex: p.signIndex,
      signLord: ZODIAC_LORDS[p.sign],
      degreeInSign: p.degreeInSign,
      degreeFormatted: p.formattedDegree,
      house,
      nakshatra: nakObj.name,
      nakshatraIndex: nakIdx,
      nakshatraLord: nakObj.lord,
      pada,
      isRetrograde: p.isRetrograde,
      isCombust,
      dignity
    };
  });

  const natalAsc = planets.find((p) => p.name === 'Ascendant');
  const trueAscDeg = ascendantDeg !== undefined ? ascendantDeg : (natalAsc ? natalAsc.longitude : ascSignIndex * 30);
  const ascDegreeInSign = trueAscDeg % 30;
  const ascNakIdx = Math.floor(trueAscDeg / (360 / 27));
  const ascNakObj = NAKSHATRAS[ascNakIdx] || { name: 'Ashwini', lord: 'Ketu' };
  const ascDegInNak = trueAscDeg % (360 / 27);
  const ascPada = Math.floor(ascDegInNak / (360 / 108)) + 1;

  const ascPlanet: PlanetPosition = {
    name: 'Ascendant',
    symbol: 'Asc',
    longitude: trueAscDeg,
    sign: ZODIAC_SIGNS[ascSignIndex],
    signIndex: ascSignIndex,
    signLord: ZODIAC_LORDS[ZODIAC_SIGNS[ascSignIndex]],
    degreeInSign: ascDegreeInSign,
    degreeFormatted: formatDegree(ascDegreeInSign),
    house: 1,
    nakshatra: ascNakObj.name,
    nakshatraIndex: ascNakIdx,
    nakshatraLord: ascNakObj.lord,
    pada: ascPada,
    isRetrograde: false,
    isCombust: false,
    dignity: 'Neutral'
  };

  const allTransitPlanets = [ascPlanet, ...transitPlanets];

  return buildChartData('Transit', 'Gochar Kundali', allTransitPlanets, ascSignIndex);
}

export interface SripatiBhavaResult {
  madhyas: number[];
  arambhas: number[];
  antas: number[];
  spans: number[];
  mcSidereal: number;
  ascSidereal: number;
}

/**
 * Calculates authentic Parashari / Sripati Bhava Sphutas (BPHS / Sripati Paddhati)
 * Uses accurate Midheaven (MC / Dashama Madhya) and Ascendant (Lagna) quadrant trisection.
 * Computes exact Arambha (start), Madhya (cusp), Anta (end), and Span for all 12 bhavas.
 * Guarantees sum of all 12 spans equals 360.0 degrees.
 */
export function calculateSripatiBhavaSphutas(
  date: Date,
  lat: number,
  lon: number,
  ayanamsha: number,
  ascendantDeg: number
): SripatiBhavaResult {
  const time = Astronomy.MakeTime(date);
  const siderealTime = Astronomy.SiderealTime(time);
  const lstHours = (siderealTime + lon / 15.0 + 24.0) % 24.0;
  const lstDeg = lstHours * 15.0;
  const rad = Math.PI / 180;
  const tilt = Astronomy.e_tilt(time);
  const eps = ((tilt && typeof tilt.tobl === 'number') ? tilt.tobl : 23.4392911) * rad;
  const theta = lstDeg * rad;

  // Tropical MC: tan(MC) = tan(theta) / cos(eps)
  const mcTropRad = Math.atan2(Math.sin(theta), Math.cos(theta) * Math.cos(eps));
  const mcTropDeg = (mcTropRad / rad + 360) % 360;
  const mcSidereal = (mcTropDeg - ayanamsha + 360) % 360;
  const ascSidereal = (ascendantDeg % 360 + 360) % 360;
  const icSidereal = (mcSidereal + 180) % 360;
  const descSidereal = (ascSidereal + 180) % 360;

  // 12 Madhyas (Mid-points / Cusps):
  // H1 = Asc, H10 = MC, H4 = IC, H7 = Desc
  const madhyas: number[] = new Array(12);
  madhyas[0] = ascSidereal;  // H1
  madhyas[9] = mcSidereal;   // H10
  madhyas[3] = icSidereal;   // H4
  madhyas[6] = descSidereal; // H7

  // Quadrant H10 -> H1
  const arc10_1 = (ascSidereal - mcSidereal + 360) % 360;
  const tri10_1 = arc10_1 / 3;
  madhyas[10] = (mcSidereal + tri10_1) % 360;       // H11
  madhyas[11] = (mcSidereal + 2 * tri10_1) % 360;   // H12

  // Quadrant H1 -> H4
  const arc1_4 = (icSidereal - ascSidereal + 360) % 360;
  const tri1_4 = arc1_4 / 3;
  madhyas[1] = (ascSidereal + tri1_4) % 360;        // H2
  madhyas[2] = (ascSidereal + 2 * tri1_4) % 360;    // H3

  // Opposites for H5, H6, H8, H9
  madhyas[4] = (madhyas[10] + 180) % 360; // H5
  madhyas[5] = (madhyas[11] + 180) % 360; // H6
  madhyas[7] = (madhyas[1] + 180) % 360;  // H8
  madhyas[8] = (madhyas[2] + 180) % 360;  // H9

  // Sandhis: Arithmetic midpoint between adjacent madhyas
  const sandhis: number[] = new Array(12);
  for (let i = 0; i < 12; i++) {
    const mCurrent = madhyas[i];
    const mNext = madhyas[(i + 1) % 12];
    const arc = (mNext - mCurrent + 360) % 360;
    sandhis[i] = (mCurrent + arc / 2) % 360;
  }

  // Arambhas, Antas, Spans
  const arambhas: number[] = new Array(12);
  const antas: number[] = new Array(12);
  const spans: number[] = new Array(12);

  for (let i = 0; i < 12; i++) {
    const prevSandhi = sandhis[(i + 11) % 12];
    const currentSandhi = sandhis[i];
    arambhas[i] = prevSandhi;
    antas[i] = currentSandhi;
    spans[i] = (currentSandhi - prevSandhi + 360) % 360;
  }

  return { madhyas, arambhas, antas, spans, mcSidereal, ascSidereal };
}

/**
 * Builds authentic Chalit Chart using Sripati Bhava boundaries
 */
export function buildChalitChart(
  planets: PlanetPosition[],
  sripati: SripatiBhavaResult,
  ascSignIndex: number
): KundaliChartData {
  const chalitPlanets: PlanetPosition[] = planets.map((p) => {
    let bhava = 1;
    for (let h = 0; h < 12; h++) {
      const arambha = sripati.arambhas[h];
      const span = sripati.spans[h];
      const diff = (p.longitude - arambha + 360) % 360;
      if (diff < span) {
        bhava = h + 1;
        break;
      }
    }
    return {
      ...p,
      house: bhava
    };
  });

  const houses: HouseInfo[] = Array.from({ length: 12 }, (_, i) => {
    const houseNumber = i + 1;
    const signIndex = (ascSignIndex + i) % 12;
    const sign = ZODIAC_SIGNS[signIndex];
    const signLord = ZODIAC_LORDS[sign];
    const planetsInHouse = chalitPlanets
      .filter((p) => p.house === houseNumber)
      .map((p) => p.name);

    return {
      houseNumber,
      sign,
      signIndex,
      signLord,
      degreeStart: sripati.arambhas[i],
      degreeFormatted: formatDegree(sripati.arambhas[i]),
      bhavaMadhya: sripati.madhyas[i],
      bhavaMadhyaFormatted: formatDegree(sripati.madhyas[i]),
      bhavaArambha: sripati.arambhas[i],
      bhavaArambhaFormatted: formatDegree(sripati.arambhas[i]),
      bhavaAnta: sripati.antas[i],
      bhavaAntaFormatted: formatDegree(sripati.antas[i]),
      bhavaSpan: sripati.spans[i],
      planetsInHouse
    };
  });

  return {
    divisionalCode: 'Chalit',
    title: 'Bhava Chalit Kundali (Sripati)',
    houses,
    planets: chalitPlanets,
    ascendantIndex: ascSignIndex
  };
}

// ============================================================================
// CANONICAL SHODASHVARGA DIVISIONAL CHARTS & SACRED DIGNITY SUBSYSTEM
// ============================================================================

/**
 * Classical BPHS 60 Shashtiamsha Deities (Brihat Parashara Hora Shastra Ch. 6, v. 39-42)
 */
export const SHASHTIAMSHA_DEITIES: Array<{
  index: number;
  name: string;
  nameHi: string;
  isBenefic: boolean;
}> = [
  { index: 1, name: 'Ghora', nameHi: 'घोर', isBenefic: false },
  { index: 2, name: 'Rakshasa', nameHi: 'राक्षस', isBenefic: false },
  { index: 3, name: 'Deva', nameHi: 'देव', isBenefic: true },
  { index: 4, name: 'Kubera', nameHi: 'कुबेर', isBenefic: true },
  { index: 5, name: 'Yaksha', nameHi: 'यक्ष', isBenefic: true },
  { index: 6, name: 'Kinnara', nameHi: 'किन्नर', isBenefic: true },
  { index: 7, name: 'Bhrashta', nameHi: 'भ्रष्ट', isBenefic: false },
  { index: 8, name: 'Kulaghna', nameHi: 'कुलघ्न', isBenefic: false },
  { index: 9, name: 'Garala', nameHi: 'गरल', isBenefic: false },
  { index: 10, name: 'Vahni', nameHi: 'वह्नि', isBenefic: false },
  { index: 11, name: 'Maya', nameHi: 'माया', isBenefic: false },
  { index: 12, name: 'Purishaka', nameHi: 'पुरीषक', isBenefic: false },
  { index: 13, name: 'Apampati', nameHi: 'अपांपति', isBenefic: true },
  { index: 14, name: 'Marutvat', nameHi: 'मारुत', isBenefic: true },
  { index: 15, name: 'Kaala', nameHi: 'काल', isBenefic: false },
  { index: 16, name: 'Sarpa', nameHi: 'सर्प', isBenefic: false },
  { index: 17, name: 'Amrita', nameHi: 'अमृत', isBenefic: true },
  { index: 18, name: 'Indu', nameHi: 'इन्दु', isBenefic: true },
  { index: 19, name: 'Mridu', nameHi: 'मृदु', isBenefic: true },
  { index: 20, name: 'Komala', nameHi: 'कोमल', isBenefic: true },
  { index: 21, name: 'Heramba', nameHi: 'हेरम्ब', isBenefic: true },
  { index: 22, name: 'Brahma', nameHi: 'ब्रह्मा', isBenefic: true },
  { index: 23, name: 'Vishnu', nameHi: 'विष्णु', isBenefic: true },
  { index: 24, name: 'Maheshwara', nameHi: 'महेश्वर', isBenefic: true },
  { index: 25, name: 'Deva', nameHi: 'देव', isBenefic: true },
  { index: 26, name: 'Ardra', nameHi: 'आर्द्र', isBenefic: true },
  { index: 27, name: 'Kalinasa', nameHi: 'कलिनाश', isBenefic: true },
  { index: 28, name: 'Kshiteesha', nameHi: 'क्षितीश', isBenefic: true },
  { index: 29, name: 'Kamalakara', nameHi: 'कमलाकर', isBenefic: true },
  { index: 30, name: 'Gulika', nameHi: 'गुलिक', isBenefic: false },
  { index: 31, name: 'Mrityu', nameHi: 'मृत्यु', isBenefic: false },
  { index: 32, name: 'Kaala', nameHi: 'काल', isBenefic: false },
  { index: 33, name: 'Davagni', nameHi: 'दावाग्नि', isBenefic: false },
  { index: 34, name: 'Ghora', nameHi: 'घोर', isBenefic: false },
  { index: 35, name: 'Yama', nameHi: 'यम', isBenefic: false },
  { index: 36, name: 'Kantaka', nameHi: 'कण्टक', isBenefic: false },
  { index: 37, name: 'Sudha', nameHi: 'सुधा', isBenefic: true },
  { index: 38, name: 'Amrita', nameHi: 'अमृत', isBenefic: true },
  { index: 39, name: 'Poornachandra', nameHi: 'पूर्णचन्द्र', isBenefic: true },
  { index: 40, name: 'Vishadagdha', nameHi: 'विषदिग्ध', isBenefic: false },
  { index: 41, name: 'Kulanasa', nameHi: 'कुलनाश', isBenefic: false },
  { index: 42, name: 'Vamshakshaya', nameHi: 'वंशक्षय', isBenefic: false },
  { index: 43, name: 'Utpata', nameHi: 'उत्पात', isBenefic: false },
  { index: 44, name: 'Kaalarupa', nameHi: 'कालरूप', isBenefic: false },
  { index: 45, name: 'Saumya', nameHi: 'सौम्य', isBenefic: true },
  { index: 46, name: 'Komala', nameHi: 'कोमल', isBenefic: true },
  { index: 47, name: 'Sheetala', nameHi: 'शीतल', isBenefic: true },
  { index: 48, name: 'Karaladamshtra', nameHi: 'करालदंष्ट्र', isBenefic: false },
  { index: 49, name: 'Chandramukhi', nameHi: 'चन्द्रमुखी', isBenefic: true },
  { index: 50, name: 'Praveena', nameHi: 'प्रवीण', isBenefic: true },
  { index: 51, name: 'Kaalpavaka', nameHi: 'कालपावक', isBenefic: false },
  { index: 52, name: 'Dandayudha', nameHi: 'दण्डायुध', isBenefic: false },
  { index: 53, name: 'Nirmala', nameHi: 'निर्मल', isBenefic: true },
  { index: 54, name: 'Shubha', nameHi: 'शुभ', isBenefic: true },
  { index: 55, name: 'Ashubha', nameHi: 'अशुभ', isBenefic: false },
  { index: 56, name: 'Atishubha', nameHi: 'अतिशुभ', isBenefic: true },
  { index: 57, name: 'Sudha', nameHi: 'सुधा', isBenefic: true },
  { index: 58, name: 'Payodhi', nameHi: 'पयोधि', isBenefic: true },
  { index: 59, name: 'Bhramana', nameHi: 'भ्रमण', isBenefic: false },
  { index: 60, name: 'Chandrarekha', nameHi: 'चन्द्ररेखा', isBenefic: true }
];

/**
 * Universal builder for divisional chart planet arrays.
 * Computes authentic divisional sign, dispositor (signLord), intra-varga scaled degree,
 * and authentic 9-tier Parashari dignity within the divisional sign.
 */
export function buildDivisionalPlanets(
  planets: PlanetPosition[],
  getVargaSign: (lon: number) => number,
  getIntraDegree?: (lon: number) => number
): { vargaPlanets: PlanetPosition[]; vargaAscSign: number } {
  const ascPlan = planets.find((p) => p.name === 'Ascendant')!;
  const vargaAscSign = getVargaSign(ascPlan.longitude);

  const prelim = planets.map((p) => {
    const s = getVargaSign(p.longitude);
    const house = ((s - vargaAscSign + 12) % 12) + 1;
    return { name: p.name as PlanetName, house };
  });

  const vargaPlanets: PlanetPosition[] = planets.map((p) => {
    const s = getVargaSign(p.longitude);
    const house = ((s - vargaAscSign + 12) % 12) + 1;
    const sign = ZODIAC_SIGNS[s];
    const signLord = ZODIAC_LORDS[sign];
    const intraDeg = getIntraDegree ? getIntraDegree(p.longitude) : (p.longitude % 30);
    const dignity = calculateDignity(p.name as PlanetName, s, intraDeg, house, prelim);

    return {
      ...p,
      signIndex: s,
      sign,
      signLord,
      degreeInSign: intraDeg,
      degreeFormatted: formatDegree(intraDeg),
      dignity,
      house
    };
  });

  return { vargaPlanets, vargaAscSign };
}

/**
 * Navamsha D9 Chart Builder
 */
function buildNavamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getNavamshaSign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const navPart = Math.floor(degInSign / (30 / 9)); // 0 to 8
    const element = signIdx % 4; // Fire (0,4,8)->0, Earth (1,5,9)->9, Air (2,6,10)->6, Water (3,7,11)->3
    const startSigns = [0, 9, 6, 3];
    return (startSigns[element] + navPart) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    const navPart = Math.floor(degInSign / (30 / 9));
    return (degInSign - navPart * (30 / 9)) * 9;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getNavamshaSign, getIntraDeg);
  return buildChartData('D9', 'Navamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Hora D2 Chart Builder (Sun/Leo in 1st half of odd signs & 2nd half of even; Moon/Cancer in 2nd half of odd & 1st half of even)
 */
function buildHoraChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getHoraSign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const isOdd = signIdx % 2 === 0; // 0=Aries (odd)
    if (degInSign < 15) {
      return isOdd ? 4 : 3; // Leo or Cancer
    } else {
      return isOdd ? 3 : 4;
    }
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 15) * 2;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getHoraSign, getIntraDeg);
  return buildChartData('D2', 'Hora Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Drekkana D3 Chart Builder (Parashari: 1st decan same, 2nd 5th, 3rd 9th)
 */
function buildDrekkanaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD3Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 10); // 0, 1, 2
    return (signIdx + part * 4) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 10) * 3;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD3Sign, getIntraDeg);
  return buildChartData('D3', 'Drekkana Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Chaturthamsha D4 Chart Builder (1st quadrant same, 2nd 4th, 3rd 7th, 4th 10th)
 */
function buildChaturthamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD4Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 7.5); // 0 to 3
    return (signIdx + part * 3) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 7.5) * 4;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD4Sign, getIntraDeg);
  return buildChartData('D4', 'Chaturthamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Saptamsha D7 Chart Builder (Odd from self, Even from 7th)
 */
function buildSaptamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD7Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / (30 / 7));
    const isOdd = signIdx % 2 === 0;
    const start = isOdd ? signIdx : (signIdx + 6) % 12;
    return (start + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / (30 / 7));
    return (degInSign - part * (30 / 7)) * 7;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD7Sign, getIntraDeg);
  return buildChartData('D7', 'Saptamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Dashamsha D10 Chart Builder (Odd from self, Even from 9th sign per BPHS Ch. 6, v. 22)
 * Remediated P0-VARGA-2 / F-VARGA-02: Even signs start from 9th sign (signIdx + 8) % 12.
 */
export function buildDashamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD10Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 3);
    const isOdd = signIdx % 2 === 0;
    const start = isOdd ? signIdx : (signIdx + 8) % 12; // 9th sign in 0-indexed arithmetic
    return (start + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 3) * 10;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD10Sign, getIntraDeg);
  return buildChartData('D10', 'Dashamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Dwadasamsha D12 Chart Builder (Count from self)
 */
function buildDwadasamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD12Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 2.5);
    return (signIdx + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 2.5) * 12;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD12Sign, getIntraDeg);
  return buildChartData('D12', 'Dwadashamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Shodashamsha D16 Chart Builder (Movable from Aries, Fixed from Leo, Dual from Sagittarius)
 */
function buildShodashamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD16Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 1.875); // 0 to 15
    const modality = signIdx % 3; // 0=Movable, 1=Fixed, 2=Dual
    const startSign = modality === 0 ? 0 : modality === 1 ? 4 : 8; // Aries, Leo, Sagittarius
    return (startSign + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 1.875) * 16;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD16Sign, getIntraDeg);
  return buildChartData('D16', 'Shodashamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Vimshamsha D20 Chart Builder (Movable from Aries, Fixed from Sagittarius, Dual from Leo)
 */
function buildVimshamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD20Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 1.5); // 0 to 19
    const modality = signIdx % 3;
    const startSign = modality === 0 ? 0 : modality === 1 ? 8 : 4; // Aries, Sagittarius, Leo
    return (startSign + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 1.5) * 20;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD20Sign, getIntraDeg);
  return buildChartData('D20', 'Vimshamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Chaturvimshamsha D24 Chart Builder (Siddhamsa: Odd from Leo, Even from Cancer)
 */
function buildChaturvimshamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD24Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 1.25); // 0 to 23
    const isOdd = signIdx % 2 === 0;
    const startSign = isOdd ? 4 : 3; // Leo or Cancer
    return (startSign + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 1.25) * 24;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD24Sign, getIntraDeg);
  return buildChartData('D24', 'Chaturvimshamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Saptavimshamsha D27 Chart Builder (Bhamsa: Fire from Aries, Earth from Cancer, Air from Libra, Water from Capricorn)
 */
function buildSaptavimshamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD27Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / (30 / 27)); // 0 to 26
    const element = signIdx % 4; // 0=Fire, 1=Earth, 2=Air, 3=Water
    const startSign = element === 0 ? 0 : element === 1 ? 3 : element === 2 ? 6 : 9;
    return (startSign + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / (30 / 27));
    return (degInSign - part * (30 / 27)) * 27;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD27Sign, getIntraDeg);
  return buildChartData('D27', 'Saptavimshamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Trishamsha D30 Chart Builder (Brihat Parashara Hora Shastra Ch. 6, v. 29-31)
 * Remediated P0-VARGA-1 / F-VARGA-01:
 * Odd Signs: Mars 5° (Aries), Saturn 5° (Aquarius), Jupiter 8° (Sagittarius), Mercury 7° (Gemini), Venus 5° (Libra)
 * Even Signs: Venus 5° (Taurus), Mercury 7° (Virgo), Jupiter 8° (Pisces), Saturn 5° (Capricorn), Mars 5° (Scorpio)
 */
export function buildTrishamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD30Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const isOdd = signIdx % 2 === 0;
    if (isOdd) {
      if (degInSign < 5) return 0; // Aries (Mars, 5°)
      if (degInSign < 10) return 10; // Aquarius (Saturn, 5°)
      if (degInSign < 18) return 8; // Sagittarius (Jupiter, 8°)
      if (degInSign < 25) return 2; // Gemini (Mercury, 7°)
      return 6; // Libra (Venus, 5°) - BPHS Ch. 6, v. 29
    } else {
      if (degInSign < 5) return 1; // Taurus (Venus, 5°) - BPHS Ch. 6, v. 30
      if (degInSign < 12) return 5; // Virgo (Mercury, 7°) - BPHS Ch. 6, v. 30
      if (degInSign < 20) return 11; // Pisces (Jupiter, 8°) - BPHS Ch. 6, v. 30
      if (degInSign < 25) return 9; // Capricorn (Saturn, 5°) - BPHS Ch. 6, v. 30
      return 7; // Scorpio (Mars, 5°) - BPHS Ch. 6, v. 30
    }
  };

  const getIntraDeg = (lon: number) => {
    const deg = lon % 30;
    const isOdd = Math.floor(lon / 30) % 2 === 0;
    if (isOdd) {
      if (deg < 5) return (deg / 5) * 30;
      if (deg < 10) return ((deg - 5) / 5) * 30;
      if (deg < 18) return ((deg - 10) / 8) * 30;
      if (deg < 25) return ((deg - 18) / 7) * 30;
      return ((deg - 25) / 5) * 30;
    } else {
      if (deg < 5) return (deg / 5) * 30;
      if (deg < 12) return ((deg - 5) / 7) * 30;
      if (deg < 20) return ((deg - 12) / 8) * 30;
      if (deg < 25) return ((deg - 20) / 5) * 30;
      return ((deg - 25) / 5) * 30;
    }
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD30Sign, getIntraDeg);
  return buildChartData('D30', 'Trimshamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Khavedamsha D40 Chart Builder (Odd from Aries, Even from Libra)
 */
function buildKhavedamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD40Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 0.75); // 0 to 39
    const isOdd = signIdx % 2 === 0;
    const startSign = isOdd ? 0 : 6; // Aries or Libra
    return (startSign + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 0.75) * 40;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD40Sign, getIntraDeg);
  return buildChartData('D40', 'Khavedamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Akshavedamsha D45 Chart Builder (Movable from Aries, Fixed from Leo, Dual from Sagittarius)
 */
function buildAkshavedamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD45Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / (30 / 45)); // 0 to 44
    const modality = signIdx % 3;
    const startSign = modality === 0 ? 0 : modality === 1 ? 4 : 8; // Aries, Leo, Sagittarius
    return (startSign + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / (30 / 45));
    return (degInSign - part * (30 / 45)) * 45;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD45Sign, getIntraDeg);
  return buildChartData('D45', 'Akshavedamsha Kundali', vargaPlanets, vargaAscSign);
}

/**
 * Shashtiamsha D60 Chart Builder (60 divisions of 0.5° each with BPHS deities)
 * Remediated P1-VARGA-3 / F-VARGA-06: Attaches authoritative BPHS 60 deities
 * with direct mapping (1-60) in odd signs and reversed mapping (60-1) in even signs.
 */
export function buildShashtiamshaChart(planets: PlanetPosition[], ascSignIndex: number): KundaliChartData {
  const getD60Sign = (lon: number) => {
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const part = Math.floor(degInSign / 0.5); // 0 to 59
    return (signIdx + part) % 12;
  };

  const getIntraDeg = (lon: number) => {
    const degInSign = lon % 30;
    return (degInSign % 0.5) * 60;
  };

  const { vargaPlanets, vargaAscSign } = buildDivisionalPlanets(planets, getD60Sign, getIntraDeg);

  // Attach authentic BPHS 60 Shashtiamsha Deity to each planet
  const d60PlanetsWithDeities: PlanetPosition[] = vargaPlanets.map((p) => {
    const signIdx = Math.floor(p.longitude / 30);
    const degInSign = p.longitude % 30;
    const part = Math.floor(degInSign / 0.5); // 0 to 59
    const isOdd = signIdx % 2 === 0;
    const deityIndex = isOdd ? (part + 1) : (60 - part); // BPHS Ch. 6, v. 42
    const deity = SHASHTIAMSHA_DEITIES[deityIndex - 1] || SHASHTIAMSHA_DEITIES[0];
    const deityInfo: ShashtiamshaDeityInfo = {
      index: deityIndex,
      name: deity.name,
      nameHi: deity.nameHi,
      isBenefic: deity.isBenefic,
      nature: deity.isBenefic ? 'Benefic' : 'Malefic'
    };
    return {
      ...p,
      shashtiamshaDeity: deityInfo
    };
  });

  return buildChartData('D60', 'Shashtiamsha Kundali', d60PlanetsWithDeities, vargaAscSign);
}

/**
 * Canonical test whether planet or Lagna is Vargottama (same sign in D1 Rashi & D9 Navamsha)
 */
export function isVargottama(d1SignIndex: number, d9SignIndex: number): boolean {
  return d1SignIndex === d9SignIndex;
}

/**
 * Canonical test whether planet is in sacred Pushkara Navamsha
 * (Fire: 7th/9th Navamsha; Earth: 3rd/5th Navamsha; Air: 6th/8th Navamsha; Water: 1st/3rd Navamsha)
 */
export function isPushkarNavamsha(d1SignIndex: number, d9SignIndex: number): boolean {
  if ([0, 4, 8].includes(d1SignIndex)) return d9SignIndex === 6 || d9SignIndex === 8;
  if ([1, 5, 9].includes(d1SignIndex)) return d9SignIndex === 11 || d9SignIndex === 1;
  if ([2, 6, 10].includes(d1SignIndex)) return d9SignIndex === 11 || d9SignIndex === 1;
  if ([3, 7, 11].includes(d1SignIndex)) return d9SignIndex === 3 || d9SignIndex === 5;
  return false;
}

const PUSHKARA_BHAGA_TARGETS: Record<number, number[]> = {
  0: [21],
  1: [14],
  2: [18, 24],
  3: [8, 19],
  4: [19],
  5: [9],
  6: [24],
  7: [11],
  8: [9, 23],
  9: [14],
  10: [19],
  11: [9]
};

/**
 * Canonical test whether degree is in sacred Pushkara Bhaga
 */
export function isPushkarBhaga(d1SignIndex: number, degreeInSign: number): boolean {
  const targets = PUSHKARA_BHAGA_TARGETS[d1SignIndex] || [];
  return targets.some((deg) => Math.abs(degreeInSign - deg) <= 1.0);
}

/**
 * Evaluates Parashari Varga Bheda attainment across 16 Shodashvargas (BPHS Ch. 6, v. 43-51)
 * Parijata = 2, Uttama = 3, Gopura = 4, Simhasana = 5, Paravata = 6, Devaloka = 7, Airavata = 8+ vargas in own/exalted/moolatrikona signs.
 */
export function calculateVargaBhedas(
  charts: CompleteKundali['charts'],
  planets: PlanetPosition[]
): VargaBhedaRecord[] {
  const vargaKeys = [
    'D1', 'D2', 'D3', 'D4', 'D7', 'D9', 'D10', 'D12',
    'D16', 'D20', 'D24', 'D27', 'D30', 'D40', 'D45', 'D60'
  ] as const;

  const results: VargaBhedaRecord[] = [];

  for (const p of planets) {
    if (p.name === 'Ascendant') continue;
    const beneficVargas: string[] = [];

    for (const vKey of vargaKeys) {
      const chart = charts[vKey];
      if (!chart) continue;
      const vPlanet = chart.planets.find((item) => item.name === p.name);
      if (!vPlanet) continue;

      const dig = vPlanet.dignity;
      if (dig === 'Own House' || dig === 'Exalted' || dig === 'Moolatrikona') {
        beneficVargas.push(vKey);
      }
    }

    const count = beneficVargas.length;
    let title: VargaBhedaRecord['vargaBhedaTitle'] = 'Ordinary';
    let titleHi = 'साधारण';

    if (count >= 8) {
      title = 'Airavata';
      titleHi = 'ऐरावत (८+ वर्ग)';
    } else if (count === 7) {
      title = 'Devaloka';
      titleHi = 'देवलोक (७ वर्ग)';
    } else if (count === 6) {
      title = 'Paravata';
      titleHi = 'पारावत (६ वर्ग)';
    } else if (count === 5) {
      title = 'Simhasana';
      titleHi = 'सिंहासन (५ वर्ग)';
    } else if (count === 4) {
      title = 'Gopura';
      titleHi = 'गोपुर (४ वर्ग)';
    } else if (count === 3) {
      title = 'Uttama';
      titleHi = 'उत्तम (३ वर्ग)';
    } else if (count === 2) {
      title = 'Parijata';
      titleHi = 'पारिजात (२ वर्ग)';
    }

    results.push({
      planet: p.name,
      auspiciousVargaCount: count,
      vargaBhedaTitle: title,
      vargaBhedaTitleHi: titleHi,
      beneficVargas
    });
  }

  return results;
}

/**
 * Manglik Dosha Analysis — Delegates to Canonical yogaDoshaEngine
 */
function calculateManglikDosha(planets: PlanetPosition[]): ManglikAnalysis {
  return evaluateManglikDosha({ planets } as CompleteKundali);
}

/**
 * Kaal Sarp Dosha Analysis — Delegates to Canonical yogaDoshaEngine
 */
function calculateKaalSarpDosha(planets: PlanetPosition[]): KaalSarpAnalysis {
  return evaluateKaalSarpYoga({ planets } as CompleteKundali);
}

export const ZODIAC_SIGNS_HI: Record<ZodiacSign, string> = {
  Aries: 'मेष',
  Taurus: 'वृषभ',
  Gemini: 'मिथुन',
  Cancer: 'कर्क',
  Leo: 'सिंह',
  Virgo: 'कन्या',
  Libra: 'तुला',
  Scorpio: 'वृश्चिक',
  Sagittarius: 'धनु',
  Capricorn: 'मकर',
  Aquarius: 'कुंभ',
  Pisces: 'मीन'
};

function formatDayMonthYear(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

function formatISODate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getSaturnTransitSignAtDate(date: Date, ayanamshaSystem?: string): { signIndex: number; sign: ZodiacSign } {
  const time = Astronomy.MakeTime(date);
  const saturnEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Saturn, time, true));
  const ayanamsha = getActiveAyanamsha(time, ayanamshaSystem);
  const sLong = (saturnEcl.elon - ayanamsha + 360) % 360;
  const signIndex = Math.floor(sLong / 30);
  return { signIndex, sign: ZODIAC_SIGNS[signIndex] };
}

/**
 * Calculates complete lifetime timeline of Saturn Sade Sati and Dhaiyya transits with exact Day/Month/Year dates
 */
export function calculateSadeSatiAndDhaiyyaTimeline(
  moonSignIdx: number,
  birthDate: Date,
  dashas: DashaPeriod[] = [],
  targetDate?: Date,
  ayanamshaSystem?: string
): SadeSatiDhaiyyaPeriod[] {
  const startYear = birthDate.getFullYear() - 1;
  const endYear = birthDate.getFullYear() + 85;

  const startDateScan = new Date(startYear, 0, 1);
  const endDateScan = new Date(endYear, 11, 31);

  interface SignStay {
    signIndex: number;
    sign: ZodiacSign;
    start: Date;
    end: Date;
  }

  const rawStays: SignStay[] = [];
  let currentStay: SignStay | null = null;
  const stepDays = 7;

  for (let t = new Date(startDateScan); t <= endDateScan; t.setDate(t.getDate() + stepDays)) {
    const curr = getSaturnTransitSignAtDate(t, ayanamshaSystem);
    if (!currentStay) {
      currentStay = {
        signIndex: curr.signIndex,
        sign: curr.sign,
        start: new Date(t),
        end: new Date(t)
      };
    } else if (currentStay.signIndex !== curr.signIndex) {
      // Find exact transition day using binary search between (t - 7 days) and t
      const prevDate = new Date(t.getTime() - stepDays * 86400000);
      let low = prevDate.getTime();
      let high = t.getTime();
      for (let s = 0; s < 7; s++) {
        const mid = Math.floor((low + high) / 2);
        const midPos = getSaturnTransitSignAtDate(new Date(mid), ayanamshaSystem);
        if (midPos.signIndex === curr.signIndex) {
          high = mid;
        } else {
          low = mid;
        }
      }
      const transitionDate = new Date(high);
      currentStay.end = new Date(transitionDate.getTime() - 86400000);
      rawStays.push(currentStay);

      currentStay = {
        signIndex: curr.signIndex,
        sign: curr.sign,
        start: transitionDate,
        end: new Date(t)
      };
    } else {
      currentStay.end = new Date(t);
    }
  }
  if (currentStay) {
    rawStays.push(currentStay);
  }

  const effectiveDate = targetDate || new Date();
  const periods: SadeSatiDhaiyyaPeriod[] = [];
  let sadeSatiCycleCounter = 1;

  rawStays.forEach((stay, idx) => {
    const diff = (stay.signIndex - moonSignIdx + 12) % 12;
    // 11 = 12th from moon (1st phase)
    // 0  = 1st from moon (2nd phase / peak)
    // 1  = 2nd from moon (3rd phase / setting)
    // 3  = 4th from moon (Kantaka Dhaiyya)
    // 7  = 8th from moon (Ashtama Dhaiyya)
    if (![11, 0, 1, 3, 7].includes(diff)) return;

    let type: SadeSatiDhaiyyaPeriod['type'];
    let category: SadeSatiDhaiyyaPeriod['category'] = 'Sade Sati';
    let phaseLabel = '';
    let nameEn = '';
    let nameHi = '';
    let descriptionEn = '';
    let descriptionHi = '';
    let impactAreaEn = '';
    let impactAreaHi = '';
    let remedyEn = '';
    let remedyHi = '';
    let cycleNumber: number | undefined = undefined;

    if (diff === 11) {
      type = 'Sade Sati (1st Phase)';
      category = 'Sade Sati';
      phaseLabel = 'Phase 1: Rising (12th from Moon)';
      nameEn = `Sade Sati — 1st Phase (Rising / Aarohi in ${stay.sign})`;
      nameHi = `साढ़े साती — प्रथम चरण (उदय / ${ZODIAC_SIGNS_HI[stay.sign]} राशि)`;
      descriptionEn = `Saturn transits the 12th house from natal Moon (${stay.sign}). Focus shifts towards inner discipline, managing expenses, foreign travel/connections, and letting go of outlived habits.`;
      descriptionHi = `शनि का जन्म चंद्र से 12वें भाव (${ZODIAC_SIGNS_HI[stay.sign]}) में गोचर। यह व्यय नियंत्रण, आध्यात्मिक चिंतन और धैर्य की परीक्षा का समय है।`;
      impactAreaEn = 'Sleep, Expenses, Travel, Solitude & Mental Restructuring';
      impactAreaHi = 'व्यय, अनिद्रा, विदेश यात्रा व मानसिक अनुशासन';
      remedyEn = 'Recite Hanuman Chalisa on Tuesdays and Saturdays; light a mustard oil lamp under a Peepal tree.';
      remedyHi = 'नित्य हनुमान चालीसा का पाठ करें एवं शनिवार को पीपल के नीचे सरसों के तेल का दीपक जलाएं।';
      cycleNumber = sadeSatiCycleCounter;
    } else if (diff === 0) {
      type = 'Sade Sati (2nd Phase / Peak)';
      category = 'Sade Sati';
      phaseLabel = 'Phase 2: Peak / Janma Shani (Over Moon)';
      nameEn = `Sade Sati — 2nd Phase (Peak / Janma Shani in ${stay.sign})`;
      nameHi = `साढ़े साती — द्वितीय चरण (मध्य / जन्म शनि - ${ZODIAC_SIGNS_HI[stay.sign]})`;
      descriptionEn = `Saturn transits directly over natal Moon (${stay.sign}). Known as the peak or core phase, requiring maximum emotional resilience, hard work, personal accountability, and ethical grounding.`;
      descriptionHi = `शनि का जन्म चंद्र के ऊपर (${ZODIAC_SIGNS_HI[stay.sign]}) गोचर। यह साढ़े साती का मुख्य शिखर काल है जो दृढ़ता, कर्मठता और आंतरिक शक्ति विकसित करता है।`;
      impactAreaEn = 'Core Mind, Health, Identity, Leadership & Heavy Responsibilities';
      impactAreaHi = 'मानसिक स्थिति, शारीरिक स्वास्थ्य, उत्तरदायित्व व कर्म क्षेत्र';
      remedyEn = 'Chant Shani Beej Mantra 108 times daily; perform Tailabhishekam on Lord Shani or offer water to Shiva Lingam.';
      remedyHi = 'ॐ शं शनैश्चराय नमः का नित्य 108 बार जप करें तथा शिवलिंग पर जल अर्पित करें।';
      cycleNumber = sadeSatiCycleCounter;
    } else if (diff === 1) {
      type = 'Sade Sati (3rd Phase)';
      category = 'Sade Sati';
      phaseLabel = 'Phase 3: Setting (2nd from Moon)';
      nameEn = `Sade Sati — 3rd Phase (Setting / Avarohi in ${stay.sign})`;
      nameHi = `साढ़े साती — तृतीय चरण (अस्त / ढलती - ${ZODIAC_SIGNS_HI[stay.sign]})`;
      descriptionEn = `Saturn transits the 2nd house from natal Moon (${stay.sign}). The setting phase consolidates karmic gains, stabilizes finances, demands mindful communication in family, and brings lasting maturity.`;
      descriptionHi = `शनि का जन्म चंद्र से द्वितीय भाव (${ZODIAC_SIGNS_HI[stay.sign]}) में गोचर। यह धन संचय, पारिवारिक सद्भाव एवं दीर्घकालिक स्थिरता का निर्माण करता है।`;
      impactAreaEn = 'Wealth Accumulation, Family Values, Diet & Speech Restraint';
      impactAreaHi = 'धन कोष, पारिवारिक संबंध, वाणी संयम व खानपान';
      remedyEn = 'Donate black sesame, urad dal, or iron items to the underprivileged on Saturdays; respect elders.';
      remedyHi = 'शनिवार को काले तिल, उड़द दाल या भोजन का दान करें तथा माता-पिता व बुजुर्गों की सेवा करें।';
      cycleNumber = sadeSatiCycleCounter;
      sadeSatiCycleCounter += 1;
    } else if (diff === 3) {
      type = 'Shani Dhaiyya (Kantaka - 4th)';
      category = 'Dhaiyya';
      phaseLabel = 'Kantaka Shani (4th from Moon)';
      nameEn = `Shani Dhaiyya — Kantaka Shani (4th from Moon in ${stay.sign})`;
      nameHi = `शनि ढैय्या — कंटक शनि (चंद्र से चतुर्थ भाव - ${ZODIAC_SIGNS_HI[stay.sign]})`;
      descriptionEn = `Saturn transits the 4th house from natal Moon (${stay.sign}) for 2.5 years. Demands attention to domestic peace, property matters, vehicle safety, mother's wellbeing, and inner emotional balance.`;
      descriptionHi = `शनि का जन्म चंद्र से चतुर्थ भाव में ढाई वर्ष का गोचर। गृह शांति, माता के स्वास्थ्य, भूमि-वाहन एवं मानसिक सुख पर ध्यान देने का समय।`;
      impactAreaEn = 'Home, Mother, Property, Vehicle & Emotional Security';
      impactAreaHi = 'गृह सुख, माता का स्वास्थ्य, भूमि-भवन व मानसिक शांति';
      remedyEn = 'Recite Dasharatha Shani Stotram on Saturdays; feed birds with grain and offer milk/water to Shiva.';
      remedyHi = 'दशरथ कृत शनि स्तोत्र का पाठ करें तथा पक्षियों को दाना और जरूरतमंदों की मदद करें।';
    } else {
      type = 'Shani Dhaiyya (Ashtama - 8th)';
      category = 'Dhaiyya';
      phaseLabel = 'Ashtama Shani (8th from Moon)';
      nameEn = `Shani Dhaiyya — Ashtama Shani (8th from Moon in ${stay.sign})`;
      nameHi = `शनि ढैय्या — अष्टम शनि (चंद्र से अष्टम भाव - ${ZODIAC_SIGNS_HI[stay.sign]})`;
      descriptionEn = `Saturn transits the 8th house from natal Moon (${stay.sign}) for 2.5 years. A period of profound transformation, overcoming obstacles, clearing debts, deep research, and awakening occult or spiritual insight.`;
      descriptionHi = `शनि का जन्म चंद्र से आठवें भाव में ढाई वर्ष का गोचर। यह गूढ़ ज्ञान, ऋण मुक्ति, स्वास्थ्य रक्षा एवं गहरे जीवन परिवर्तनों का काल है।`;
      impactAreaEn = 'Transformation, Health Mindfulness, Occult Wisdom & Overcoming Sudden Hurdles';
      impactAreaHi = 'आकस्मिक परिवर्तन, स्वास्थ्य सजगता, आयु-आरोग्य व गूढ़ विद्या';
      remedyEn = 'Recite Maha Mrityunjaya Mantra daily; light mustard oil lamp on Saturdays and avoid hasty financial speculations.';
      remedyHi = 'महामृत्युंजय मंत्र का नित्य जप करें, शनिवार को सरसों के तेल का दीपक लगाएं और जोखिम भरे निर्णयों से बचें।';
    }

    const startMs = stay.start.getTime();
    const endMs = stay.end.getTime();
    const nowMs = effectiveDate.getTime();

    let status: 'Past' | 'Active' | 'Upcoming' = 'Upcoming';
    if (nowMs > endMs) {
      status = 'Past';
    } else if (nowMs >= startMs && nowMs <= endMs) {
      status = 'Active';
    }

    const durationYears = Number(((endMs - startMs) / (365.25 * 86400000)).toFixed(1));
    const totalMonths = Math.round(((endMs - startMs) / (30.4375 * 86400000)));
    const yrs = Math.floor(totalMonths / 12);
    const mos = totalMonths % 12;
    const durationFormatted = yrs > 0 ? `${yrs} Yrs ${mos > 0 ? `${mos} Mos` : ''}`.trim() : `${mos} Mos`;

    // Find coinciding Vimshottari Dasha
    const overlappingDashas: string[] = [];
    dashas.forEach((md) => {
      const mdStart = new Date(md.startDate).getTime();
      const mdEnd = new Date(md.endDate).getTime();
      if (mdStart <= endMs && mdEnd >= startMs) {
        if (md.antardashas && md.antardashas.length > 0) {
          md.antardashas.forEach((ad) => {
            const adStart = new Date(ad.startDate).getTime();
            const adEnd = new Date(ad.endDate).getTime();
            if (adStart <= endMs && adEnd >= startMs) {
              overlappingDashas.push(`${md.planet}-${ad.planet}`);
            }
          });
        } else {
          overlappingDashas.push(`${md.planet}`);
        }
      }
    });

    const uniqueDashas = Array.from(new Set(overlappingDashas));
    const coincidingDasha = uniqueDashas.length > 0 
      ? (uniqueDashas.length <= 2 ? uniqueDashas.join(', ') : `${uniqueDashas[0]} ... ${uniqueDashas[uniqueDashas.length - 1]}`)
      : undefined;

    periods.push({
      id: `shani-${stay.signIndex}-${stay.start.getFullYear()}-${idx}`,
      type,
      category,
      nameEn,
      nameHi,
      phaseLabel,
      saturnSign: stay.sign,
      saturnSignHi: ZODIAC_SIGNS_HI[stay.sign],
      houseFromMoon: diff === 11 ? 12 : diff === 0 ? 1 : diff === 1 ? 2 : diff === 3 ? 4 : 8,
      startDate: formatISODate(stay.start),
      endDate: formatISODate(stay.end),
      formattedStartDate: formatDayMonthYear(stay.start),
      formattedEndDate: formatDayMonthYear(stay.end),
      durationYears,
      durationFormatted,
      status,
      cycleNumber,
      coincidingDasha,
      descriptionEn,
      descriptionHi,
      impactAreaEn,
      impactAreaHi,
      remedyEn,
      remedyHi
    });
  });

  return periods;
}

/**
 * Sade Sati & Shani Dhaiyya Status Analysis with Full Timeline
 */
export function calculateSadeSati(
  natalPlanets: PlanetPosition[],
  transitSaturn?: { signIndex: number; sign: ZodiacSign },
  birthDate?: Date,
  dashas: DashaPeriod[] = [],
  targetDate?: Date,
  ayanamshaSystem?: string
): SadeSatiAnalysis {
  const moon = natalPlanets.find((p) => p.name === 'Moon')!;
  const saturnSignIdx = transitSaturn ? transitSaturn.signIndex : natalPlanets.find((p) => p.name === 'Saturn')!.signIndex;
  const saturnSign = transitSaturn ? transitSaturn.sign : natalPlanets.find((p) => p.name === 'Saturn')!.sign;

  const moonSignIdx = moon.signIndex;
  const diff = (saturnSignIdx - moonSignIdx + 12) % 12;

  let isInSadeSati = false;
  let isDhaiyya = false;
  let phase: SadeSatiAnalysis['phase'] = 'Not Active';
  let totalPeriod = 'N/A';

  if (diff === 11) {
    isInSadeSati = true;
    phase = 'Rising Phase (1st)';
    totalPeriod = 'Approx. 7.5 Years (1st / Rising Phase)';
  } else if (diff === 0) {
    isInSadeSati = true;
    phase = 'Peak Phase (2nd)';
    totalPeriod = 'Approx. 7.5 Years (2nd / Peak Phase)';
  } else if (diff === 1) {
    isInSadeSati = true;
    phase = 'Setting Phase (3rd)';
    totalPeriod = 'Approx. 7.5 Years (3rd / Setting Phase)';
  } else if (diff === 3) {
    isDhaiyya = true;
    phase = 'Dhaiyya (4th)';
    totalPeriod = 'Approx. 2.5 Years (Kantaka Shani - 4th from Moon)';
  } else if (diff === 7) {
    isDhaiyya = true;
    phase = 'Dhaiyya (8th)';
    totalPeriod = 'Approx. 2.5 Years (Ashtama Shani - 8th from Moon)';
  }

  const remedies = [
    'Recite Hanuman Chalisa daily and Shani Chalisa on Saturdays.',
    'Donate mustard oil, black sesame seeds, and iron items to needy people on Saturdays.',
    'Light a mustard oil lamp under a Peepal tree on Saturday evenings.',
    'Worship Lord Shani and perform Tailabhishekam on Saturdays.'
  ];

  // Calculate complete lifetime timeline of Sade Sati & Dhaiyya periods with exact Day/Month/Year
  const safeBirthDate = birthDate || new Date(1990, 0, 1);
  const timeline = calculateSadeSatiAndDhaiyyaTimeline(moonSignIdx, safeBirthDate, dashas, targetDate, ayanamshaSystem);
  const activePeriodDetails = timeline.find((p) => p.status === 'Active');

  let description = isInSadeSati 
    ? `Saturn is currently transiting ${saturnSign}, forming ${phase} of Sade Sati relative to your natal Moon in ${moon.sign}${activePeriodDetails ? ` (${activePeriodDetails.formattedStartDate} to ${activePeriodDetails.formattedEndDate})` : ''}. This 7.5-year cycle fosters discipline and spiritual evolution.`
    : isDhaiyya
    ? `Saturn is currently transiting ${saturnSign}, forming ${phase} (Shani Dhaiyya) relative to your natal Moon in ${moon.sign}${activePeriodDetails ? ` (${activePeriodDetails.formattedStartDate} to ${activePeriodDetails.formattedEndDate})` : ''}. This 2.5-year period demands focus and care.`
    : `Saturn is in ${saturnSign}, which is currently favorable or neutral relative to your natal Moon in ${moon.sign}.`;

  return {
    isInSadeSati,
    isDhaiyya,
    phase,
    saturnSign,
    moonSign: moon.sign,
    description,
    totalPeriod,
    remedies,
    activePeriodDetails,
    timeline
  };
}

/**
 * Pitra Dosha Analysis
 */
function calculatePitraDosha(planets: PlanetPosition[]): PitraDoshaAnalysis {
  const sun = planets.find((p) => p.name === 'Sun')!;
  const rahu = planets.find((p) => p.name === 'Rahu')!;
  const ketu = planets.find((p) => p.name === 'Ketu')!;
  const saturn = planets.find((p) => p.name === 'Saturn')!;

  const reasons: string[] = [];

  if (sun.house === rahu.house) reasons.push('Sun is conjunct Rahu (Eclipse Yoga in birth chart).');
  if (sun.house === ketu.house) reasons.push('Sun is conjunct Ketu.');
  if (sun.house === saturn.house) reasons.push('Sun is conjunct Saturn.');
  if (sun.house === 9) reasons.push('Malefic planet located in the 9th house of ancestors/dharma.');

  const hasPitraDosha = reasons.length > 0;
  let severity: PitraDoshaAnalysis['severity'] = 'None';
  if (reasons.length === 1) severity = 'Mild';
  else if (reasons.length === 2) severity = 'Moderate';
  else if (reasons.length >= 3) severity = 'Severe';

  const remedies = [
    'Offer Tarpan and perform Shradha rituals during Pitru Paksha for ancestral blessings.',
    'Water a Peepal tree every Saturday and offer prayers.',
    'Donate food and clothes to Brahmins and poor people on Amavasya (New Moon).',
    'Recite Aditya Hridaya Stotra daily for Solar energy.'
  ];

  return {
    hasPitraDosha,
    severity,
    reasons,
    remedies
  };
}

/**
 * Gemstone & Rudraksha Recommendations (Chart-Specific Analysis with Dasha Timing)
 */
export function getGemstoneRecommendations(
  ascSignIndex: number, 
  planets: PlanetPosition[], 
  dashas: DashaPeriod[]
): GemstoneRecommendation[] {
  const GEM_MAP: {
    [key: number]: {
      life: GemstoneRecommendation;
      lucky: GemstoneRecommendation;
      fortune: GemstoneRecommendation;
    };
  } = {
    0: { // Aries (Mars, Sun, Jupiter)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Red Coral (Moonga)',
        substitute: 'Red Agate',
        planet: 'Mars',
        metal: 'Copper / Gold',
        finger: 'Ring Finger',
        wearingDay: 'Tuesday Morning',
        mantra: 'Om Bhaumaya Namah',
        benefits: 'Enhances energy, physical vitality, confidence, and leadership.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Ruby (Manik)',
        substitute: 'Red Garnet',
        planet: 'Sun',
        metal: 'Gold / Copper',
        finger: 'Ring Finger',
        wearingDay: 'Sunday Morning',
        mantra: 'Om Suryaya Namah',
        benefits: 'Brings divine intellect, creative brilliance, authority, and luck.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Yellow Sapphire (Pukhraj)',
        substitute: 'Yellow Topaz',
        planet: 'Jupiter',
        metal: 'Gold / Brass',
        finger: 'Index Finger',
        wearingDay: 'Thursday Morning',
        mantra: 'Om Gram Greem Groum Sah Gurave Namah',
        benefits: 'Brings spiritual wisdom, higher education, destiny favor, and expansion.'
      }
    },
    1: { // Taurus (Venus, Mercury, Saturn)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Diamond (Heera)',
        substitute: 'White Zircon / Opal',
        planet: 'Venus',
        metal: 'Platinum / Silver / White Gold',
        finger: 'Middle / Little Finger',
        wearingDay: 'Friday Morning',
        mantra: 'Om Shum Shukraya Namah',
        benefits: 'Enhances luxury, beauty, artistic talents, and overall grace.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Emerald (Panna)',
        substitute: 'Peridot / Green Tourmaline',
        planet: 'Mercury',
        metal: 'Gold / Silver',
        finger: 'Little Finger',
        wearingDay: 'Wednesday Morning',
        mantra: 'Om Bum Budhaya Namah',
        benefits: 'Sharpens business acumen, memory, and communication skills.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Blue Sapphire (Neelam)',
        substitute: 'Amethyst / Blue Topaz',
        planet: 'Saturn',
        metal: 'Iron / Silver',
        finger: 'Middle Finger',
        wearingDay: 'Saturday Evening',
        mantra: 'Om Sham Shanicharaya Namah',
        benefits: 'Brings stability, discipline, success in technical fields, and luck.'
      }
    },
    2: { // Gemini (Mercury, Venus, Saturn)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Emerald (Panna)',
        substitute: 'Peridot',
        planet: 'Mercury',
        metal: 'Gold',
        finger: 'Little Finger',
        wearingDay: 'Wednesday Morning',
        mantra: 'Om Budhaya Namah',
        benefits: 'Improves intelligence, adaptability, and business success.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Diamond (Heera)',
        substitute: 'White Sapphire / Opal',
        planet: 'Venus',
        metal: 'Silver',
        finger: 'Middle Finger',
        wearingDay: 'Friday Morning',
        mantra: 'Om Shukraya Namah',
        benefits: 'Enhances creativity, love, comfort, and entertainment.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Blue Sapphire (Neelam)',
        substitute: 'Iolite',
        planet: 'Saturn',
        metal: 'Iron',
        finger: 'Middle Finger',
        wearingDay: 'Saturday Evening',
        mantra: 'Om Shanishcharaya Namah',
        benefits: 'Brings divine protection, focus, and long-term career growth.'
      }
    },
    3: { // Cancer (Moon, Mars, Jupiter)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Pearl (Moti)',
        substitute: 'Moonstone',
        planet: 'Moon',
        metal: 'Silver',
        finger: 'Little Finger',
        wearingDay: 'Monday Morning',
        mantra: 'Om Chandraya Namah',
        benefits: 'Calms the mind, improves emotional stability and family harmony.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Red Coral (Moonga)',
        substitute: 'Red Agate',
        planet: 'Mars',
        metal: 'Copper',
        finger: 'Ring Finger',
        wearingDay: 'Tuesday Morning',
        mantra: 'Om Bhaumaya Namah',
        benefits: 'Increases courage, speculative gains, and physical energy.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Yellow Sapphire (Pukhraj)',
        substitute: 'Yellow Topaz',
        planet: 'Jupiter',
        metal: 'Gold',
        finger: 'Index Finger',
        wearingDay: 'Thursday Morning',
        mantra: 'Om Gurave Namah',
        benefits: 'Brings wisdom, spiritual growth, and expansive luck.'
      }
    },
    4: { // Leo (Sun, Mars, Jupiter)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Ruby (Manik)',
        substitute: 'Red Garnet',
        planet: 'Sun',
        metal: 'Gold',
        finger: 'Ring Finger',
        wearingDay: 'Sunday Morning',
        mantra: 'Om Suryaya Namah',
        benefits: 'Enhances self-esteem, leadership, vitality, and social status.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Yellow Sapphire (Pukhraj)',
        substitute: 'Citrine',
        planet: 'Jupiter',
        metal: 'Gold',
        finger: 'Index Finger',
        wearingDay: 'Thursday Morning',
        mantra: 'Om Jhram Jhreem Jroum Sah Gurave Namah',
        benefits: 'Brings wisdom, spiritual intuition, intellect, and creative brilliance.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Red Coral (Moonga)',
        substitute: 'Red Carnelian',
        planet: 'Mars',
        metal: 'Copper',
        finger: 'Ring Finger',
        wearingDay: 'Tuesday Morning',
        mantra: 'Om Kram Kreem Kroum Sah Bhaumaya Namah',
        benefits: 'Increases courage, luck in undertakings, and dynamic protective leadership.'
      }
    },
    5: { // Virgo (Mercury, Venus, Saturn)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Emerald (Panna)',
        substitute: 'Green Tourmaline',
        planet: 'Mercury',
        metal: 'Gold',
        finger: 'Little Finger',
        wearingDay: 'Wednesday Morning',
        mantra: 'Om Budhaya Namah',
        benefits: 'Enhances logic, health, and communication efficiency.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Blue Sapphire (Neelam)',
        substitute: 'Amethyst',
        planet: 'Saturn',
        metal: 'Steel / Iron',
        finger: 'Middle Finger',
        wearingDay: 'Saturday Evening',
        mantra: 'Om Shanishcharaya Namah',
        benefits: 'Brings stability, removes obstacles, and ensures hard-earned success.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Diamond (Heera)',
        substitute: 'White Zircon',
        planet: 'Venus',
        metal: 'White Gold / Silver',
        finger: 'Middle Finger',
        wearingDay: 'Friday Morning',
        mantra: 'Om Shukraya Namah',
        benefits: 'Attracts wealth, artistic success, and comfortable living.'
      }
    },
    6: { // Libra (Venus, Saturn, Mercury)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Diamond (Heera)',
        substitute: 'White Sapphire',
        planet: 'Venus',
        metal: 'Silver',
        finger: 'Little Finger',
        wearingDay: 'Friday Morning',
        mantra: 'Om Shukraya Namah',
        benefits: 'Enhances personality, charm, luxury, and relationship harmony.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Blue Sapphire (Neelam)',
        substitute: 'Iolite',
        planet: 'Saturn',
        metal: 'Iron',
        finger: 'Middle Finger',
        wearingDay: 'Saturday Evening',
        mantra: 'Om Shanishcharaya Namah',
        benefits: 'Brings creative focus, speculative gains, and children\'s success.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Emerald (Panna)',
        substitute: 'Peridot',
        planet: 'Mercury',
        metal: 'Gold',
        finger: 'Little Finger',
        wearingDay: 'Wednesday Morning',
        mantra: 'Om Budhaya Namah',
        benefits: 'Improves communication, trade, and overall good fortune.'
      }
    },
    7: { // Scorpio (Mars, Jupiter, Moon)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Red Coral (Moonga)',
        substitute: 'Red Jasper',
        planet: 'Mars',
        metal: 'Copper',
        finger: 'Ring Finger',
        wearingDay: 'Tuesday Morning',
        mantra: 'Om Bhaumaya Namah',
        benefits: 'Enhances determination, immunity, and protection from enemies.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Yellow Sapphire (Pukhraj)',
        substitute: 'Yellow Topaz',
        planet: 'Jupiter',
        metal: 'Gold',
        finger: 'Index Finger',
        wearingDay: 'Thursday Morning',
        mantra: 'Om Gurave Namah',
        benefits: 'Brings spiritual depth, higher wisdom, and noble children.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Pearl (Moti)',
        substitute: 'Moonstone',
        planet: 'Moon',
        metal: 'Silver',
        finger: 'Little Finger',
        wearingDay: 'Monday Morning',
        mantra: 'Om Chandraya Namah',
        benefits: 'Calms emotions and brings sudden luck and divine intuition.'
      }
    },
    8: { // Sagittarius (Jupiter, Mars, Sun)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Yellow Sapphire (Pukhraj)',
        substitute: 'Citrine',
        planet: 'Jupiter',
        metal: 'Gold',
        finger: 'Index Finger',
        wearingDay: 'Thursday Morning',
        mantra: 'Om Gurave Namah',
        benefits: 'Enhances wisdom, optimism, general prosperity, and longevity.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Red Coral (Moonga)',
        substitute: 'Red Agate',
        planet: 'Mars',
        metal: 'Copper',
        finger: 'Ring Finger',
        wearingDay: 'Tuesday Morning',
        mantra: 'Om Bhaumaya Namah',
        benefits: 'Boosts energy, courage, and analytical problem-solving.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Ruby (Manik)',
        substitute: 'Red Garnet',
        planet: 'Sun',
        metal: 'Gold',
        finger: 'Ring Finger',
        wearingDay: 'Sunday Morning',
        mantra: 'Om Suryaya Namah',
        benefits: 'Brings divine luck, authority, and professional heights.'
      }
    },
    9: { // Capricorn (Saturn, Venus, Mercury)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Blue Sapphire (Neelam)',
        substitute: 'Amethyst',
        planet: 'Saturn',
        metal: 'Iron',
        finger: 'Middle Finger',
        wearingDay: 'Saturday Evening',
        mantra: 'Om Shanishcharaya Namah',
        benefits: 'Enhances discipline, work ethics, stability, and career focus.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Diamond (Heera)',
        substitute: 'White Zircon',
        planet: 'Venus',
        metal: 'Silver',
        finger: 'Little Finger',
        wearingDay: 'Friday Morning',
        mantra: 'Om Shukraya Namah',
        benefits: 'Attracts creativity, artistic success, and luxury.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Emerald (Panna)',
        substitute: 'Peridot',
        planet: 'Mercury',
        metal: 'Gold',
        finger: 'Little Finger',
        wearingDay: 'Wednesday Morning',
        mantra: 'Om Budhaya Namah',
        benefits: 'Brings trade success, higher education, and financial fortune.'
      }
    },
    10: { // Aquarius (Saturn, Mercury, Venus)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Blue Sapphire (Neelam)',
        substitute: 'Lapis Lazuli',
        planet: 'Saturn',
        metal: 'Silver / Iron',
        finger: 'Middle Finger',
        wearingDay: 'Saturday Evening',
        mantra: 'Om Shanishcharaya Namah',
        benefits: 'Provides structure, protection, and long-term success.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Emerald (Panna)',
        substitute: 'Green Onyx',
        planet: 'Mercury',
        metal: 'Gold',
        finger: 'Little Finger',
        wearingDay: 'Wednesday Morning',
        mantra: 'Om Budhaya Namah',
        benefits: 'Enhances communication, logic, and intelligent speculative gains.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Diamond (Heera)',
        substitute: 'Opal / White Topaz',
        planet: 'Venus',
        metal: 'Silver / Platinum',
        finger: 'Middle Finger',
        wearingDay: 'Friday Morning',
        mantra: 'Om Shukraya Namah',
        benefits: 'Brings immense luxury, fortune, and harmonious social life.'
      }
    },
    11: { // Pisces (Jupiter, Moon, Mars)
      life: {
        type: 'Life Stone (Lagna)',
        gemstone: 'Yellow Sapphire (Pukhraj)',
        substitute: 'Yellow Topaz',
        planet: 'Jupiter',
        metal: 'Gold',
        finger: 'Index Finger',
        wearingDay: 'Thursday Morning',
        mantra: 'Om Gurave Namah',
        benefits: 'Brings divine wisdom, health, and spiritual enlightenment.'
      },
      lucky: {
        type: 'Lucky Stone (5th House)',
        gemstone: 'Pearl (Moti)',
        substitute: 'Moonstone',
        planet: 'Moon',
        metal: 'Silver',
        finger: 'Little Finger',
        wearingDay: 'Monday Morning',
        mantra: 'Om Chandraya Namah',
        benefits: 'Calms mind, enhances intuition, and improves progeny success.'
      },
      fortune: {
        type: 'Fortune Stone (9th House)',
        gemstone: 'Red Coral (Moonga)',
        substitute: 'Red Agate',
        planet: 'Mars',
        metal: 'Copper',
        finger: 'Ring Finger',
        wearingDay: 'Tuesday Morning',
        mantra: 'Om Bhaumaya Namah',
        benefits: 'Increases courage, ensures professional fortune, and removes obstacles.'
      }
    }
  };

  const matched = GEM_MAP[ascSignIndex];
  if (!matched) return [];

  const rawRecs = [matched.life, matched.lucky, matched.fortune];

  // Refine with Chart-Specific Logic
  return rawRecs.map(rec => {
    const planet = planets.find(p => p.name === rec.planet);
    if (!planet) return rec;

    let score = 85; // Base suitability for Lagna/5th/9th lords
    let statusParts: string[] = [];

    // Analyze Position
    statusParts.push(`${planet.name} is in House ${planet.house} (${planet.sign})`);
    
    if (planet.dignity === 'Exalted') {
      score += 15;
      statusParts.push('Highly auspicious exalted state.');
    } else if (planet.dignity === 'Debilitated') {
      score -= 30;
      statusParts.push('Debilitated (Weak) status - Wear with caution or consult expert.');
    } else if (planet.dignity === 'Own House') {
      score += 10;
      statusParts.push('Strong in its own sign.');
    }

    if (planet.isCombust) {
      score -= 10;
      statusParts.push('Planet is Combust (Close to Sun).');
    }
    
    if (planet.isRetrograde) {
      statusParts.push('Planet is Retrograde (Intense results).');
    }

    // House Analysis (Dusthana check)
    if ([6, 8, 12].includes(planet.house)) {
      score -= 15;
      statusParts.push(`Located in a Dusthana (H${planet.house}). Primary remedy should be charity/mantras.`);
    }

    return {
      ...rec,
      chartStatus: statusParts.join(' | '),
      suitabilityScore: Math.min(100, Math.max(0, score)),
      auspiciousDashas: findAuspiciousDashaPeriods(rec.planet, dashas)
    };
  });
}

/**
 * Finds the upcoming or current auspicious dasha windows for a specific planet
 */
function findAuspiciousDashaPeriods(planetName: string, dashas: DashaPeriod[]) {
  // Find Mahadasha
  const md = dashas.find(d => d.planet === planetName);
  
  // Find current/upcoming Antardasha within ANY Mahadasha if possible
  // In Vedic tradition, the planet's own Antardasha within its own Mahadasha is potent,
  // but also its Antardasha within friendly planets' Mahadashas.
  // For simplicity, we'll highlight its primary Mahadasha and its current/next Antardasha.
  
  let mdStr = md ? `${md.startDate} to ${md.endDate}` : 'N/A';
  
  // Find the next Antardasha of this planet
  let adStr = 'N/A';
  let pdStr = 'N/A'; // For PD, we'll just look at the current MD's nested ADs if any
  
  const nowMs = Date.now();
  
  // Search through all MDs to find current/future ADs of this planet
  for (const m of dashas) {
    const ad = m.antardashas?.find(a => a.planet === planetName);
    if (ad) {
      const endMs = new Date(ad.endDate).getTime();
      if (endMs > nowMs) {
        adStr = `${ad.startDate} to ${ad.endDate}`;
        break; 
      }
    }
  }

  return {
    md: mdStr,
    ad: adStr,
    pd: planetName === 'Saturn' ? 'Special focus during Sade Sati phases' : undefined
  };
}

/**
 * Classical Brihat Parashara Hora Shastra (BPHS) Ashtakavarga Engine
 * 8 Contributing Entities (7 physical planets + Ascendant/Lagna)
 * 7 Bhinna Ashtakavarga (BAV) Charts (Sun to Saturn)
 * Total Sarvashtakavarga (SAV) = Exactly 337 Bindus
 */
export type BAVPlanet = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn';
export type BAVContributor = BAVPlanet | 'Ascendant';

export const BPHS_ASHTAKAVARGA_RULES: Record<BAVPlanet, Record<BAVContributor, number[]>> = {
  Sun: {
    Sun: [1, 2, 4, 7, 8, 9, 10, 11],
    Moon: [3, 6, 10, 11],
    Mars: [1, 2, 4, 7, 8, 9, 10, 11],
    Mercury: [3, 5, 6, 9, 10, 11, 12],
    Jupiter: [5, 6, 9, 11],
    Venus: [6, 7, 12],
    Saturn: [1, 2, 4, 7, 8, 9, 10, 11],
    Ascendant: [3, 4, 6, 10, 11, 12],
  },
  Moon: {
    Sun: [3, 6, 7, 8, 10, 11],
    Moon: [1, 3, 6, 7, 10, 11],
    Mars: [2, 3, 5, 6, 9, 10, 11],
    Mercury: [1, 3, 4, 5, 7, 8, 10, 11],
    Jupiter: [1, 4, 7, 8, 10, 11, 12],
    Venus: [3, 4, 5, 7, 9, 10, 11],
    Saturn: [3, 5, 6, 11],
    Ascendant: [3, 6, 10, 11],
  },
  Mars: {
    Sun: [3, 5, 6, 10, 11],
    Moon: [3, 6, 11],
    Mars: [1, 2, 4, 7, 8, 10, 11],
    Mercury: [3, 5, 6, 11],
    Jupiter: [6, 10, 11, 12],
    Venus: [6, 8, 11, 12],
    Saturn: [1, 4, 7, 8, 9, 10, 11],
    Ascendant: [1, 3, 6, 10, 11],
  },
  Mercury: {
    Sun: [5, 6, 9, 11, 12],
    Moon: [2, 4, 6, 8, 10, 11],
    Mars: [1, 2, 4, 7, 8, 9, 10, 11],
    Mercury: [1, 3, 5, 6, 9, 10, 11, 12],
    Jupiter: [6, 8, 11, 12],
    Venus: [1, 2, 3, 4, 5, 8, 9, 11],
    Saturn: [1, 2, 4, 7, 8, 9, 10, 11],
    Ascendant: [1, 2, 4, 6, 8, 10, 11],
  },
  Jupiter: {
    Sun: [1, 2, 3, 4, 7, 8, 9, 10, 11],
    Moon: [2, 5, 7, 9, 11],
    Mars: [1, 2, 4, 7, 8, 10, 11],
    Mercury: [1, 2, 4, 5, 6, 9, 10, 11],
    Jupiter: [1, 2, 3, 4, 7, 8, 10, 11],
    Venus: [2, 5, 6, 9, 10, 11],
    Saturn: [3, 5, 6, 12],
    Ascendant: [1, 2, 4, 5, 6, 7, 9, 10, 11],
  },
  Venus: {
    Sun: [8, 11, 12],
    Moon: [1, 2, 3, 4, 5, 8, 9, 11, 12],
    Mars: [3, 5, 6, 9, 11, 12],
    Mercury: [3, 5, 6, 9, 11],
    Jupiter: [5, 8, 9, 10, 11],
    Venus: [1, 2, 3, 4, 5, 8, 9, 10, 11],
    Saturn: [3, 4, 5, 8, 9, 10, 11],
    Ascendant: [1, 2, 3, 4, 5, 8, 9, 11],
  },
  Saturn: {
    Sun: [1, 2, 4, 7, 8, 10, 11],
    Moon: [3, 6, 11],
    Mars: [3, 5, 6, 10, 11, 12],
    Mercury: [6, 8, 9, 10, 11, 12],
    Jupiter: [5, 6, 11, 12],
    Venus: [6, 11, 12],
    Saturn: [3, 5, 6, 11],
    Ascendant: [1, 3, 4, 6, 10, 11],
  },
};

export function calculateAshtakvarga(planets: PlanetPosition[], ascSignIndex?: number): AshtakvargaData {
  const planetSignMap = new Map<string, number>();
  planets.forEach((p) => {
    planetSignMap.set(p.name, p.signIndex);
  });

  const ascSign = ascSignIndex !== undefined 
    ? ascSignIndex 
    : (planetSignMap.get('Ascendant') ?? 0);
  planetSignMap.set('Ascendant', ascSign);

  const bhinnaBySign: { [key in BAVPlanet]: number[] } = {
    Sun: new Array(12).fill(0),
    Moon: new Array(12).fill(0),
    Mars: new Array(12).fill(0),
    Mercury: new Array(12).fill(0),
    Jupiter: new Array(12).fill(0),
    Venus: new Array(12).fill(0),
    Saturn: new Array(12).fill(0),
  };

  const targetPlanets: BAVPlanet[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const contributors: BAVContributor[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Ascendant'];

  targetPlanets.forEach((targetPlanet) => {
    contributors.forEach((contributor) => {
      const contributorSign = planetSignMap.get(contributor);
      if (contributorSign !== undefined) {
        const beneficOffsets = BPHS_ASHTAKAVARGA_RULES[targetPlanet][contributor];
        beneficOffsets.forEach((houseOffset) => {
          // BPHS rule: houseOffset is counted from contributor's sign placement (1-based offset)
          // Depositing benefic bindu directly into target Zodiac Sign Index (0 = Aries ... 11 = Pisces)
          const targetSignIndex = (contributorSign + (houseOffset - 1)) % 12;
          bhinnaBySign[targetPlanet][targetSignIndex] += 1;
        });
      }
    });
  });

  // Calculate canonical Sarvashtakvarga (SAV) across all 12 signs (sum strictly 337)
  const sarvashtakvargaBySign = new Array(12).fill(0);
  for (let s = 0; s < 12; s++) {
    sarvashtakvargaBySign[s] = targetPlanets.reduce((sum, p) => sum + bhinnaBySign[p][s], 0);
  }

  // Derive House-Indexed representations (0 = House 1 ... 11 = House 12 from Natal Lagna)
  const bhinnaByHouse: { [key in BAVPlanet]: number[] } = {
    Sun: new Array(12).fill(0),
    Moon: new Array(12).fill(0),
    Mars: new Array(12).fill(0),
    Mercury: new Array(12).fill(0),
    Jupiter: new Array(12).fill(0),
    Venus: new Array(12).fill(0),
    Saturn: new Array(12).fill(0),
  };
  const sarvashtakvargaByHouse = new Array(12).fill(0);

  for (let h = 0; h < 12; h++) {
    const signForHouse = (ascSign + h) % 12;
    sarvashtakvargaByHouse[h] = sarvashtakvargaBySign[signForHouse];
    targetPlanets.forEach(p => {
      bhinnaByHouse[p][h] = bhinnaBySign[p][signForHouse];
    });
  }

  return {
    bySign: {
      sarvashtakvarga: sarvashtakvargaBySign,
      bhinna: bhinnaBySign as any
    },
    byHouse: {
      sarvashtakvarga: sarvashtakvargaByHouse,
      bhinna: bhinnaByHouse as any
    },
    // Top-level canonical representation is strictly SIGN-INDEXED (0 = Aries ... 11 = Pisces)
    bhinna: bhinnaBySign as any,
    sarvashtakvarga: sarvashtakvargaBySign,
  };
}

/**
 * Resolves Sarvashtakavarga (SAV) bindus for a specific Zodiac Sign (0 = Aries ... 11 = Pisces).
 */
export function getSavBySign(ashtakvarga: AshtakvargaData | number[] | undefined, signIndex: number): number {
  if (!ashtakvarga) return 28;
  const normSign = ((signIndex % 12) + 12) % 12;
  if (Array.isArray(ashtakvarga)) {
    return ashtakvarga[normSign] ?? 28;
  }
  if (ashtakvarga.bySign?.sarvashtakvarga) {
    return ashtakvarga.bySign.sarvashtakvarga[normSign] ?? 28;
  }
  return ashtakvarga.sarvashtakvarga?.[normSign] ?? 28;
}

/**
 * Resolves Sarvashtakavarga (SAV) bindus for a specific Bhava (1 to 12).
 * Explicitly resolves Bhava -> Sign Index occupying that Bhava -> SAV[signIndex].
 */
export function getSavByBhava(
  ashtakvarga: AshtakvargaData | number[] | undefined,
  bhavaNumber: number,
  housesOrLagnaSign: HouseInfo[] | number
): number {
  if (!ashtakvarga) return 28;
  const normBhava = Math.max(1, Math.min(12, bhavaNumber));

  if (!Array.isArray(ashtakvarga) && ashtakvarga.byHouse?.sarvashtakvarga) {
    return ashtakvarga.byHouse.sarvashtakvarga[normBhava - 1] ?? 28;
  }

  let signIndex: number;
  if (Array.isArray(housesOrLagnaSign)) {
    const houseObj = housesOrLagnaSign.find(h => h.houseNumber === normBhava);
    signIndex = houseObj ? houseObj.signIndex : normBhava - 1;
  } else {
    signIndex = ((housesOrLagnaSign + (normBhava - 1)) % 12);
  }

  return getSavBySign(ashtakvarga, signIndex);
}

/**
 * Resolves Bhinna Ashtakavarga (BAV) bindus for a planet in a specific Zodiac Sign (0 = Aries ... 11 = Pisces).
 */
export function getBavBySign(ashtakvarga: AshtakvargaData | undefined, planet: PlanetName, signIndex: number): number {
  if (!ashtakvarga) return 4;
  const normSign = ((signIndex % 12) + 12) % 12;
  const list = ashtakvarga.bySign?.bhinna?.[planet] || ashtakvarga.bhinna?.[planet];
  return list?.[normSign] ?? 4;
}

/**
 * Resolves Bhinna Ashtakavarga (BAV) bindus for a planet in a specific Bhava (1 to 12).
 */
export function getBavByBhava(
  ashtakvarga: AshtakvargaData | undefined,
  planet: PlanetName,
  bhavaNumber: number,
  housesOrLagnaSign: HouseInfo[] | number
): number {
  if (!ashtakvarga) return 4;
  const normBhava = Math.max(1, Math.min(12, bhavaNumber));

  if (ashtakvarga.byHouse?.bhinna?.[planet]) {
    return ashtakvarga.byHouse.bhinna[planet]![normBhava - 1] ?? 4;
  }

  let signIndex: number;
  if (Array.isArray(housesOrLagnaSign)) {
    const houseObj = housesOrLagnaSign.find(h => h.houseNumber === normBhava);
    signIndex = houseObj ? houseObj.signIndex : normBhava - 1;
  } else {
    signIndex = ((housesOrLagnaSign + (normBhava - 1)) % 12);
  }
  return getBavBySign(ashtakvarga, planet, signIndex);
}

/**
 * 8 Kakshya Subdivisions per Zodiac Sign (3°45' each = 3.75°)
 * Ordered by classical mean motion: Saturn, Jupiter, Mars, Sun, Venus, Mercury, Moon, Lagna.
 */
export const KAKSHYA_LORDS: (PlanetName | 'Ascendant')[] = [
  'Saturn',    // 0°00' - 3°45'
  'Jupiter',   // 3°45' - 7°30'
  'Mars',      // 7°30' - 11°15'
  'Sun',       // 11°15' - 15°00'
  'Venus',     // 15°00' - 18°45'
  'Mercury',   // 18°45' - 22°30'
  'Moon',      // 22°30' - 26°15'
  'Ascendant'  // 26°15' - 30°00'
];

export const KAKSHYA_SPANS: string[] = [
  "0°00' - 3°45'",
  "3°45' - 7°30'",
  "7°30' - 11°15'",
  "11°15' - 15°00'",
  "15°00' - 18°45'",
  "18°45' - 22°30'",
  "22°30' - 26°15'",
  "26°15' - 30°00'"
];

export function calculateKakshyaTransitInfo(
  planet: PlanetName,
  longitude: number,
  prastaraContributors?: Record<string, number[]>
): KakshyaTransitInfo {
  const normLon = ((longitude % 360) + 360) % 360;
  const transitSignIndex = Math.floor(normLon / 30);
  const transitSign = ZODIAC_SIGNS[transitSignIndex];
  const transitDegreeInSign = normLon % 30;

  const kakshyaIndex = Math.min(7, Math.floor(transitDegreeInSign / 3.75));
  const kakshyaLord = KAKSHYA_LORDS[kakshyaIndex];
  const kakshyaDegreeSpan = KAKSHYA_SPANS[kakshyaIndex];

  let hasBindu = false;
  let binduScore = 0;
  if (prastaraContributors) {
    const contributorRow = prastaraContributors[kakshyaLord];
    if (contributorRow && contributorRow[transitSignIndex] === 1) {
      hasBindu = true;
      binduScore = 1;
    }
  }

  return {
    planet,
    transitSign,
    transitSignIndex,
    transitDegreeInSign,
    kakshyaIndex,
    kakshyaLord,
    kakshyaDegreeSpan,
    hasBindu,
    binduScore
  };
}


/**
 * Parashari Shadbala & Bhava Bala Engine
 * Re-exported from canonical implementation in ./shadbalaEngine (BPHS Ch. 27-29)
 */
export { calculateShadbala } from './shadbalaEngine';


/**
 * Jaimini Chara Karakas Engine
 */
export function calculateCharaKarakas(planets: PlanetPosition[]): CharaKarakaItem[] {
  const sevenPlanets = planets.filter((p) =>
    ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].includes(p.name)
  );

  // Sort planets descending by degree within sign (0 to 30 deg)
  const sorted = [...sevenPlanets].sort((a, b) => b.degreeInSign - a.degreeInSign);

  const KARAKA_ROLES: {
    code: CharaKarakaItem['code'];
    name: string;
    role: string;
    significatorFor: string;
  }[] = [
    { code: 'AK', name: 'Atmakaraka', role: 'Soul King & Spiritual Purpose', significatorFor: 'Self, Core Destiny & Highest Dharma' },
    { code: 'AmK', name: 'Amatyakaraka', role: 'Prime Minister & Career', significatorFor: 'Profession, Status, Wealth & Intellect' },
    { code: 'BK', name: 'Bhratrukaraka', role: 'Brother & Guru/Guide', significatorFor: 'Siblings, Mentors, Courage & Teachers' },
    { code: 'MK', name: 'Matrukaraka', role: 'Mother & Domestic Bliss', significatorFor: 'Mother, Vehicles, Fixed Assets & Peace' },
    { code: 'PK', name: 'Putrakaraka', role: 'Progeny & Genius', significatorFor: 'Children, Creativity, Mantras & Higher Learning' },
    { code: 'GK', name: 'Gnatikaraka', role: 'Kinsmen & Obstacles', significatorFor: 'Competitors, Health, Disputes & Karma' },
    { code: 'DK', name: 'DaraKaraka', role: 'Spouse & Companion', significatorFor: 'Marriage, Spouse, Life Partner & Public Business' }
  ];

  return sorted.slice(0, 7).map((p, idx) => {
    const kMeta = KARAKA_ROLES[idx];
    return {
      planet: p.name,
      code: kMeta.code,
      name: kMeta.name,
      degree: p.degreeInSign,
      formattedDegree: p.degreeFormatted,
      role: kMeta.role,
      significatorFor: kMeta.significatorFor
    };
  });
}

/**
 * Planetary Avasthas Engine
 */
export function calculateAvasthas(planets: PlanetPosition[]): PlanetaryAvastha[] {
  return planets
    .filter((p) => !['Ascendant'].includes(p.name))
    .map((p) => {
      const deg = p.degreeInSign;
      const isOdd = p.signIndex % 2 === 0; // Aries = index 0 (odd sign)

      let avastha: PlanetaryAvastha['avastha'] = 'Yuva (Adult)';
      let percentage = 100;
      let effect = 'Full, robust planetary manifestations and powerful results.';

      if (isOdd) {
        if (deg < 6) {
          avastha = 'Bala (Infant)';
          percentage = 25;
          effect = 'Mild, emerging planetary power (25% manifestation).';
        } else if (deg < 12) {
          avastha = 'Kumara (Youth)';
          percentage = 50;
          effect = 'Active, developing results (50% manifestation).';
        } else if (deg < 18) {
          avastha = 'Yuva (Adult)';
          percentage = 100;
          effect = 'Full, prime planetary strength (100% manifestation).';
        } else if (deg < 24) {
          avastha = 'Vriddha (Old)';
          percentage = 15;
          effect = 'Declining planetary vigor; passive effects.';
        } else {
          avastha = 'Mrita (Deceased)';
          percentage = 0;
          effect = 'Dormant planetary strength; relies on dispositor.';
        }
      } else {
        if (deg < 6) {
          avastha = 'Mrita (Deceased)';
          percentage = 0;
          effect = 'Dormant planetary strength; relies on dispositor.';
        } else if (deg < 12) {
          avastha = 'Vriddha (Old)';
          percentage = 15;
          effect = 'Declining planetary vigor; passive effects.';
        } else if (deg < 18) {
          avastha = 'Yuva (Adult)';
          percentage = 100;
          effect = 'Full, prime planetary strength (100% manifestation).';
        } else if (deg < 24) {
          avastha = 'Kumara (Youth)';
          percentage = 50;
          effect = 'Active, developing results (50% manifestation).';
        } else {
          avastha = 'Bala (Infant)';
          percentage = 25;
          effect = 'Mild, emerging planetary power (25% manifestation).';
        }
      }

      return {
        planet: p.name,
        avastha,
        percentage,
        effect
      };
    });
}

/**
 * Prashna (Horary) Astrology Engine (Parashara / Krishnamurti System)
 */
export function generatePrashnaKundali(seedNumber: number, category: string): PrashnaResult {
  // Prashna seed number 1..249 maps to exact zodiac degrees (360 / 249 = 1.4457 deg per seed)
  const prashnaLon = ((seedNumber - 1) * 360) / 249;
  const signIdx = Math.floor(prashnaLon / 30);
  const lagnaSign = ZODIAC_SIGNS[signIdx];
  const lagnaLord = ZODIAC_LORDS[lagnaSign];

  // Simulating live moon horary placement based on seed
  const moonSignIdx = (signIdx + (seedNumber % 7)) % 12;
  const moonSign = ZODIAC_SIGNS[moonSignIdx];
  const moonHouse = ((moonSignIdx - signIdx + 12) % 12) + 1;

  let targetHouse = 1;
  if (category === 'Career & Job') targetHouse = 10;
  else if (category === 'Marriage & Love') targetHouse = 7;
  else if (category === 'Health & Disease Recovery') targetHouse = 6;
  else if (category === 'Finance & Investments') targetHouse = 2;
  else if (category === 'Higher Studies & Visa') targetHouse = 9;
  else if (category === 'Property & Home') targetHouse = 4;
  else if (category === 'Children & Education') targetHouse = 5;

  const targetSign = ZODIAC_SIGNS[(signIdx + (targetHouse - 1)) % 12];
  const targetLord = ZODIAC_LORDS[targetSign];

  // Compute Horary Score
  let verdictScore = 75;
  const keyFactors: string[] = [];

  keyFactors.push(`Prashna Lagna falls in ${lagnaSign} (Lord: ${lagnaLord}).`);
  keyFactors.push(`Target House for ${category} is House ${targetHouse} (${targetSign}, Lord: ${targetLord}).`);

  if ([1, 5, 9, 10, 11].includes(moonHouse)) {
    verdictScore += 15;
    keyFactors.push(`Horary Moon is well-placed in House ${moonHouse} (${moonSign}), lending emotional clarity and positive timing.`);
  } else if ([6, 8, 12].includes(moonHouse)) {
    verdictScore -= 20;
    keyFactors.push(`Horary Moon in dusthana House ${moonHouse} warns of initial delays or doubts.`);
  }

  if (lagnaLord === targetLord) {
    verdictScore += 20;
    keyFactors.push(`Lagna Lord and Target House Lord are identical (${lagnaLord}), forming a powerful direct accomplishment yoga!`);
  }

  let verdict: PrashnaResult['verdict'] = 'Favorable with Effort';
  if (verdictScore >= 85) verdict = 'Highly Favorable';
  else if (verdictScore >= 65) verdict = 'Favorable with Effort';
  else if (verdictScore >= 45) verdict = 'Delayed / Obstacles';
  else verdict = 'Unfavorable';

  const remedies = [
    `Chant the Beej Mantra of ${lagnaLord} 108 times before starting new initiatives.`,
    `Worship Lord Ganesha to remove obstacles in ${category.toLowerCase()} endeavors.`,
    `Perform a light oil lamp offering on the day of ${lagnaLord}'s planetary day.`
  ];

  return {
    horaryNumber: seedNumber,
    prashnaTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    category,
    lagnaSign,
    lagnaLord,
    moonSign,
    moonHouse,
    targetHouse,
    verdict,
    verdictScore,
    keyFactors,
    remedies
  };
}

/**
 * Calculates high-precision live planetary positions (sidereal ephemeris) for any target date
 */
export function calculateLivePlanetaryEphemeris(
  targetDate: Date = new Date(),
  context?: CalculationContext | NodeConvention
): {
  planet: PlanetName;
  sign: ZodiacSign;
  signIndex: number;
  degreeInSign: number;
  formattedDegree: string;
  longitude: number;
  isRetrograde: boolean;
}[] {
  const nodeConvention: NodeConvention =
    typeof context === 'string' ? context : (context?.nodeConvention || 'mean');
  const ayanamshaSystem = typeof context === 'object' && context !== null ? context.ayanamsha : undefined;

  const time = Astronomy.MakeTime(targetDate);
  const ayanamsha = getActiveAyanamsha(time, ayanamshaSystem);

  const sunEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, time, true));
  const moonEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Moon, time, true));
  const marsEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Mars, time, true));
  const mercuryEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Mercury, time, true));
  const jupiterEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Jupiter, time, true));
  const venusEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Venus, time, true));
  const saturnEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Saturn, time, true));

  // Velocity check for retrograde motion
  const timePlus = Astronomy.MakeTime(new Date(targetDate.getTime() + 900000));
  const marsPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Mars, timePlus, true));
  const mercuryPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Mercury, timePlus, true));
  const jupiterPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Jupiter, timePlus, true));
  const venusPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Venus, timePlus, true));
  const saturnPlus = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Saturn, timePlus, true));

  const j2000Days = time.ut;
  let rahuTropicalDeg = (125.04452 - 0.0529538083 * j2000Days + 3600) % 360;

  if (nodeConvention === 'true') {
    // True (Oscillating) Lunar Node adjustment using periodic solar-lunar perturbations
    const sunMeanLong = (280.46646 + 0.98564736 * j2000Days) % 360;
    const rad = Math.PI / 180;
    const correction = -1.4979 * Math.sin(2 * (sunMeanLong - rahuTropicalDeg) * rad)
                       - 0.1500 * Math.sin((357.5291 + 0.98560028 * j2000Days) * rad);
    rahuTropicalDeg = (rahuTropicalDeg + correction + 360) % 360;
  }
  const rahuSidereal = (rahuTropicalDeg - ayanamsha + 360) % 360;
  const ketuSidereal = (rahuSidereal + 180) % 360;

  const isRetrograde = (current: number, future: number) => {
    let diff = future - current;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return diff < 0;
  };

  const rawList: { planet: PlanetName; lon: number; retro: boolean }[] = [
    { planet: 'Sun', lon: (sunEcl.elon - ayanamsha + 360) % 360, retro: false },
    { planet: 'Moon', lon: (moonEcl.elon - ayanamsha + 360) % 360, retro: false },
    { planet: 'Mars', lon: (marsEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(marsEcl.elon, marsPlus.elon) },
    { planet: 'Mercury', lon: (mercuryEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(mercuryEcl.elon, mercuryPlus.elon) },
    { planet: 'Jupiter', lon: (jupiterEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(jupiterEcl.elon, jupiterPlus.elon) },
    { planet: 'Venus', lon: (venusEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(venusEcl.elon, venusPlus.elon) },
    { planet: 'Saturn', lon: (saturnEcl.elon - ayanamsha + 360) % 360, retro: isRetrograde(saturnEcl.elon, saturnPlus.elon) },
    { planet: 'Rahu', lon: rahuSidereal, retro: true },
    { planet: 'Ketu', lon: ketuSidereal, retro: true }
  ];

  return rawList.map((r) => {
    const signIndex = Math.floor(r.lon / 30);
    const sign = ZODIAC_SIGNS[signIndex];
    const degreeInSign = r.lon % 30;
    return {
      planet: r.planet,
      sign,
      signIndex,
      degreeInSign,
      formattedDegree: formatDegree(degreeInSign),
      longitude: r.lon,
      isRetrograde: r.retro
    };
  });
}

/**
 * Calculates Live Gochar (Current Planetary Transits) dynamically relative to Natal Lagna & Moon
 */
export function calculateGocharTransits(
  natalPlanets: PlanetPosition[],
  transitDate: Date = new Date(),
  context?: CalculationContext | NodeConvention
): GocharTransitData {
  const natalAsc = natalPlanets.find((p) => p.name === 'Ascendant') || { signIndex: 0 };
  const natalMoon = natalPlanets.find((p) => p.name === 'Moon') || { signIndex: 0 };

  const ascSignIdx = natalAsc.signIndex;
  const moonSignIdx = natalMoon.signIndex;

  // Compute exact dynamic real-time planetary positions using Astronomy engine
  const liveEphemeris = calculateLivePlanetaryEphemeris(transitDate, context);

  // Classical Vedha (Obstruction) evaluation for all transiting planets from Natal Moon
  const allVedha = evaluateAllGocharVedha(
    moonSignIdx,
    liveEphemeris.map((tp) => ({ planet: tp.planet, sign: tp.sign, signIndex: tp.signIndex }))
  );

  // Benefic Transit Houses from Moon Sign according to Parashari Jyotish
  const BENEFIC_HOUSES_FROM_MOON: { [key in PlanetName]?: number[] } = {
    Sun: [3, 6, 10, 11],
    Moon: [1, 3, 6, 7, 10, 11],
    Mars: [3, 6, 11],
    Mercury: [2, 4, 6, 8, 10, 11],
    Jupiter: [2, 5, 7, 9, 11],
    Venus: [1, 2, 3, 4, 5, 8, 9, 11, 12],
    Saturn: [3, 6, 11],
    Rahu: [3, 6, 11],
    Ketu: [3, 6, 11]
  };

  const keyHighlights: string[] = [];

  const items: GocharItem[] = liveEphemeris.map((tp) => {
    const currentSign = tp.sign;
    const houseFromLagna = ((tp.signIndex - ascSignIdx + 12) % 12) + 1;
    const houseFromMoon = ((tp.signIndex - moonSignIdx + 12) % 12) + 1;

    const nakIdx = Math.floor(tp.longitude / (360 / 27));
    const nakObj = NAKSHATRAS[nakIdx] || { name: 'Ashwini', lord: 'Ketu' };
    const degInNak = tp.longitude % (360 / 27);
    const pada = Math.floor(degInNak / (360 / 108)) + 1;
    const kakshyaInfo = calculateKakshyaTransitInfo(tp.planet, tp.longitude);
    const vedha = allVedha[tp.planet];

    const beneficList = BENEFIC_HOUSES_FROM_MOON[tp.planet] || [3, 6, 11];
    const isBeneficTransitFromMoon = beneficList.includes(houseFromMoon);

    let vedicEffect = `${tp.planet} transits House ${houseFromMoon} from Moon and House ${houseFromLagna} from Lagna in ${currentSign} (${tp.formattedDegree}).`;
    if (isBeneficTransitFromMoon) {
      if (vedha && vedha.hasVedha) {
        vedicEffect += ` Classical Vedha obstruction active: Benefic potential modified/obstructed by ${vedha.obstructingPlanet} in House ${vedha.obstructingHouseFromMoon} (Phaladeepika Ch. 26).`;
      } else if (vedha && vedha.isExempted) {
        vedicEffect += ` Highly auspicious benefic transit (Gochar Shubha Phala)! (Filial exemption active: ${vedha.exemptionReason}).`;
      } else {
        vedicEffect += ` Highly auspicious benefic transit (Gochar Shubha Phala)! Unobstructed.`;
      }
    } else {
      vedicEffect += ` Requires patience and mindfulness regarding house significations.`;
    }

    if (tp.planet === 'Jupiter' && isBeneficTransitFromMoon) {
      if (vedha && vedha.hasVedha) {
        keyHighlights.push(`Transit Jupiter in ${currentSign} (House ${houseFromMoon} from Moon) is auspicious but currently obstructed by ${vedha.obstructingPlanet} via Vedha.`);
      } else {
        keyHighlights.push(`Transit Jupiter in ${currentSign} (House ${houseFromMoon} from Moon) brings wisdom, expansion, and major fortune!`);
      }
    }
    if (tp.planet === 'Saturn' && [1, 2, 12].includes(houseFromMoon)) {
      keyHighlights.push(`Transit Saturn in ${currentSign} forms Sade Sati transit (House ${houseFromMoon} from Moon).`);
    } else if (tp.planet === 'Saturn' && [4, 8].includes(houseFromMoon)) {
      keyHighlights.push(`Transit Saturn in ${currentSign} forms Dhaiya / Kantaka Shani transit.`);
    }

    return {
      planet: tp.planet,
      currentSign,
      degreeInSign: tp.degreeInSign,
      formattedDegree: tp.formattedDegree,
      isRetrograde: tp.isRetrograde,
      houseFromLagna,
      houseFromMoon,
      isBeneficTransitFromMoon,
      vedicEffect,
      nakshatra: nakObj.name,
      nakshatraLord: nakObj.lord,
      pada,
      kakshyaLord: kakshyaInfo.kakshyaLord,
      vedhaStatus: vedha
        ? {
            hasVedha: vedha.hasVedha,
            obstructingPlanet: vedha.obstructingPlanet,
            obstructingHouseFromMoon: vedha.obstructingHouseFromMoon,
            obstructingSign: vedha.obstructingSign,
            isExempted: vedha.isExempted,
            exemptionReason: vedha.exemptionReason,
            factualStatus: vedha.factualStatus
          }
        : undefined
    };
  });

  return {
    transitDate: transitDate.toISOString().split('T')[0],
    items,
    keyHighlights
  };
}

